import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent } from './components/ui/card';
import { Button } from './components/ui/button';
import { Input } from './components/ui/input';
import { 
  BookOpen, Users, Check, X, Clock, ArrowLeft, Save, Plus, Calendar, Trash2, Upload, FileText, ChevronDown, User, ExternalLink
} from 'lucide-react';
import { toast } from 'sonner';
import { KlassenbuchWoche, OffeneTageHinweis, kohorteFuer } from './components/KlassenbuchWoche';
import { useAuth } from './contexts/AuthContext';
import { TagStatusHinweis, EinwilligungBadge, TeilweiseFelder, TagAktionen, downloadPdf } from './components/AnwesenheitTag';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from './components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './components/ui/select';

const API = process.env.REACT_APP_BACKEND_URL ? `${process.env.REACT_APP_BACKEND_URL}/api` : '/api';

// Die Kurse - zusammengeführte Varianten (BG + Selbstzahler in einem)
const KURSE = [
  { 
    id: "pflegeassistent_lg1_lg2", 
    name: "LG1 & LG2 (inkl. BG)", 
    kurzname: "LG1 & LG2",
    // Zusammenführung: Zeigt auch Teilnehmer aus pflegeassistent_lg1_lg2_bg
    includeIds: ["pflegeassistent_lg1_lg2", "pflegeassistent_lg1_lg2_bg"]
  },
  { 
    id: "pflegehilfskraft_betreuungsassistent", 
    name: "Pflegehilfskraft & Betreuungsassistent (inkl. BG)", 
    kurzname: "Pflegehilfskraft",
    // Zusammenführung: Zeigt auch Teilnehmer aus pflegehilfskraft_bg
    includeIds: ["pflegehilfskraft_betreuungsassistent", "pflegehilfskraft_bg"]
  },
  { id: "palliativ_care", name: "Palliativ Care", kurzname: "Palliativ Care" },
  { id: "wundexperte", name: "Wundexperte", kurzname: "Wundexperte" },
  { id: "zusatzqualifikation_wunden", name: "Chron. Wunden (Pflegefachkräfte)", kurzname: "Chron. Wunden" },
  { id: "lg3_lg4", name: "LG3 & LG4", kurzname: "LG3 & LG4" },
  { id: "ambulanter_pflegeassistent", name: "Ambulanter Pflegeassistent", kurzname: "Amb. Pflegeassistent" },
  { id: "alltagsbegleiter_betreuungskraft", name: "Alltagsbegleiter / Betreuungskraft", kurzname: "Alltagsbegleiter" },
  { id: "pflegedienstleitung", name: "Pflegedienstleitung PDL", kurzname: "PDL" },
  { id: "praxisanleitung", name: "Praxisanleitung", kurzname: "Praxisanleitung" },
  { id: "rezertifizierung_praxisanleiter", name: "Rezertifizierung Praxisanleiter", kurzname: "Rezerti. PA" },
  { id: "rezertifizierung_betreuungskraft", name: "Rezertifizierung Betreuungskraft", kurzname: "Rezerti. BK" },
  { id: "berufsspezifisches_deutsch", name: "Berufsspezifisches Deutsch", kurzname: "Deutsch Pflege" },
  { id: "inklusionsassistenz", name: "Inklusionsassistenz", kurzname: "Inklusion" },
  { id: "gerontopsychiatrie", name: "Gerontopsychiatrie für Fachkräfte", kurzname: "Gerontopsychiatrie" },
  { id: "intensivpflege", name: "Basiskurs Intensivpflege §132l", kurzname: "Intensivpflege" },
  { id: "pflegeberater", name: "Pflegeberater §7a SGB XI", kurzname: "Pflegeberater" },
  { id: "qualitaetsbeauftragter", name: "Qualitätsbeauftragter in der Pflege", kurzname: "QM-Beauftragter" },
];

// Kurs-Mapping
const mapKursToId = (kursName) => {
  if (!kursName) return null;
  const lower = kursName.toLowerCase();
  
  if (lower.includes('gerontopsychiatrie') || lower.includes('geronto')) return 'gerontopsychiatrie';
  if (lower.includes('ambulant') && lower.includes('pflegeassistent')) return 'ambulanter_pflegeassistent';
  if (lower.includes('pflegehilfskraft') || (lower.includes('betreuungsassistent') && !lower.includes('rezert'))) return 'pflegehilfskraft_betreuungsassistent';
  if ((lower.includes('lg1') || lower.includes('lg 1')) && (lower.includes('lg2') || lower.includes('lg 2'))) {
    if (lower.includes('bildungsgutschein') || lower.includes('(bg)')) return 'pflegeassistent_lg1_lg2_bg';
    return 'pflegeassistent_lg1_lg2';
  }
  if (lower.includes('deutsch')) return 'berufsspezifisches_deutsch';
  if (lower.includes('inklusion')) return 'inklusionsassistenz';
  if (lower.includes('palliativ')) return 'palliativ_care';
  if (lower.includes('alltagsbegleiter') || (lower.includes('betreuungskraft') && lower.includes('klein'))) return 'alltagsbegleiter_betreuungskraft';
  if (lower.includes('praxisanleit') && !lower.includes('rezert')) return 'praxisanleitung';
  if ((lower.includes('lg3') || lower.includes('lg 3')) && (lower.includes('lg4') || lower.includes('lg 4'))) return 'lg3_lg4';
  if (lower.includes('wundexperte')) return 'wundexperte';
  if (lower.includes('wund') || lower.includes('chronisch')) return 'zusatzqualifikation_wunden';
  if (lower.includes('rezert') && lower.includes('betreuung')) return 'rezertifizierung_betreuungskraft';
  if (lower.includes('rezert') && lower.includes('praxis')) return 'rezertifizierung_praxisanleiter';
  if (lower.includes('pdl') || lower.includes('pflegedienstleitung')) return 'pflegedienstleitung';
  if (lower.includes('intensivpflege') || lower.includes('132l')) return 'intensivpflege';
  if (lower.includes('pflegeberater') || lower.includes('7a') || (lower.includes('§7') && !lower.includes('§72') && !lower.includes('§75'))) return 'pflegeberater';
  if (lower.includes('qualitätsbeauftragt') || lower.includes('qualitaetsbeauftragt') || lower.includes('qm-beauftragt') || lower.includes('qmb')) return 'qualitaetsbeauftragter';
  
  return null;
};

// Hilfsfunktion: Prüft ob ein Teilnehmer Bildungsgutschein hat
const istBildungsgutschein = (teilnehmer) => {
  if (!teilnehmer) return false;
  // Prüfe verschiedene Felder
  if (teilnehmer.bildungsgutschein_vorhanden === true) return true;
  if (teilnehmer.bg_sz === 'BG') return true;
  if (teilnehmer.finanzierungsart === 'bildungsgutschein') return true;
  if (teilnehmer.zahlungsart === 'bildungsgutschein') return true;
  return false;
};

// BG-fähige Kurse (nur diese können Bildungsgutschein haben)
const BG_FAEHIGE_KURSE = [
  'pflegehilfskraft_betreuungsassistent',
  'pflegehilfskraft_bg',
  'pflegeassistent_lg1_lg2',
  'pflegeassistent_lg1_lg2_bg',
  'ambulanter_pflegeassistent'
];

// Die 3 BG-Maßnahmen – im Klassenbuch direkt umschaltbar
const BG_KURSE = ['pflegeassistent_lg1_lg2', 'pflegehilfskraft_betreuungsassistent', 'ambulanter_pflegeassistent'];

const DOZENT_OPTIONEN = ['Dickie (MD)', 'Müller (JM)', 'Lorke (AL)'];
const dozentOption = (name = '') => DOZENT_OPTIONEN.find(o => name && o.split(' ')[0].toLowerCase() === name.split(' ').slice(-1)[0].toLowerCase()) || '';

export default function KlassenbuchNeu() {
  const { user } = useAuth();
  const [reloadKey, setReloadKey] = useState(0);
  const [uePlan, setUePlan] = useState(null);
  const [selectedKurs, setSelectedKurs] = useState(null);
  const [teilnehmer, setTeilnehmer] = useState([]);
  const [loading, setLoading] = useState(false);
  const [kursTeilnehmerCount, setKursTeilnehmerCount] = useState({});
  
  // Datum
  const [datum, setDatum] = useState(new Date().toISOString().split('T')[0]);
  
  // Anwesenheit: { teilnehmer_id: 'anwesend' | 'entschuldigt' | 'fehlend' }
  const [anwesenheit, setAnwesenheit] = useState({});
  const [anwDetails, setAnwDetails] = useState({});
  const [einwilligungen, setEinwilligungen] = useState({});
  const [tagDoc, setTagDoc] = useState(null);
  
  // UE-Einträge für den Tag (mehrere möglich)
  const [ueEintraege, setUeEintraege] = useState([
    { ue: 1, thema: '', dozent: '', notizen: '' }
  ]);
  
  // Import-Dialog State
  const [showImportDialog, setShowImportDialog] = useState(false);
  const [importStartDatum, setImportStartDatum] = useState('');
  const [importDozent, setImportDozent] = useState('Dickie (MD)');
  const [vorlagen, setVorlagen] = useState(null);
  const [importing, setImporting] = useState(false);
  const [themenVorlage, setThemenVorlage] = useState([]); // Liste aller Themen aus Vorlage
  const [selectedThema, setSelectedThema] = useState(null); // Ausgewähltes Tages-Thema
  // Historisches Klassenbuch
  const [showHistorischDialog, setShowHistorischDialog] = useState(false);
  const [kursDurchgaenge, setKursDurchgaenge] = useState([]);
  const [selectedDurchgang, setSelectedDurchgang] = useState(null);
  const [historischDozent, setHistorischDozent] = useState('Dickie (MD)');
  const [historischStartDatum, setHistorischStartDatum] = useState(''); // NEU: Manuelles Startdatum
  const [historischEndDatum, setHistorischEndDatum] = useState(''); // NEU: Manuelles Enddatum
  const [creatingHistorisch, setCreatingHistorisch] = useState(false);
  const [selectedKursTyp, setSelectedKursTyp] = useState('pflegehilfskraft_betreuungsassistent'); // Kurstyp für historisches Klassenbuch
  const [selectedVorlageId, setSelectedVorlageId] = useState('pfh'); // Vorlage für historisches Klassenbuch
  // Dozenten-Zuweisungs-Modal (nach Import)
  const [showDozentenDialog, setShowDozentenDialog] = useState(false);
  const [dozentenZuKurs, setDozentenZuKurs] = useState(null); // { kurs_id, tage_total, kurs_name }
  const [verfuegbareDozenten, setVerfuegbareDozenten] = useState([]);
  const [bulkZuweisungen, setBulkZuweisungen] = useState([{ tag_von: 1, tag_bis: 1, dozent: 'MD' }]);
  const [savingDozenten, setSavingDozenten] = useState(false);
  const [downloadPdfsAfterCreate, setDownloadPdfsAfterCreate] = useState(false); // PDFs nach Erstellung herunterladen — Standard AUS (Cloudflare-520-Schutz). User kann manuell aktivieren.
  const [savePdfsToTeilnehmer, setSavePdfsToTeilnehmer] = useState(true); // PDFs bei Teilnehmern speichern (Background-Task)
  const [searchTeilnehmer, setSearchTeilnehmer] = useState(''); // Suche nach Teilnehmername
  const [selectedEinzelTeilnehmer, setSelectedEinzelTeilnehmer] = useState(null); // NEU: Einzelner Teilnehmer für PDF-Download
  
  // NEU: Vorschau-Funktionalität
  const [showVorschauDialog, setShowVorschauDialog] = useState(false);
  const [vorschauData, setVorschauData] = useState(null);
  const [loadingVorschau, setLoadingVorschau] = useState(false);
  
  // NEU: Bildungsgutschein-Filter
  const [showOnlyBG, setShowOnlyBG] = useState(false);

  // NEU: Modus-Switch für Vorschau
  // false = Komplettes Soll-Klassenbuch (z.B. immer 32 Tage für LG1/LG2) — auch wenn Kurs noch läuft
  // true  = Nur bis heute (Zwischenstands-Auszug)
  const [nurBisHeute, setNurBisHeute] = useState(false);
  
  // NEU: Einzelnen Teilnehmer Klassenbuch bearbeiten
  const [showTeilnehmerDialog, setShowTeilnehmerDialog] = useState(false);
  const [selectedTeilnehmerForEdit, setSelectedTeilnehmerForEdit] = useState(null);
  const [teilnehmerAnwesenheitEdit, setTeilnehmerAnwesenheitEdit] = useState([]);
  const [loadingTeilnehmerKlassenbuch, setLoadingTeilnehmerKlassenbuch] = useState(false);

  useEffect(() => {
    loadTeilnehmerCounts();
  }, []);

  const loadTeilnehmerCounts = async () => {
    try {
      const response = await axios.get(`${API}/teilnehmer?limit=10000`);
      const alleTeilnehmer = response.data;
      
      const counts = {};
      KURSE.forEach(k => counts[k.id] = 0);
      
      const heute = new Date().toISOString().split('T')[0];
      
      // Set um doppelte Zählungen zu vermeiden (wenn ein TN in mehreren zusammengeführten IDs ist)
      const gezaehlteTN = {};
      KURSE.forEach(k => gezaehlteTN[k.id] = new Set());
      
      alleTeilnehmer.forEach(t => {
        // 1. Hauptkurs prüfen
        const kursId = mapKursToId(t.kurs);
        
        // Finde den Haupt-Kurs-Eintrag (für zusammengeführte Kurse)
        const zielKurs = KURSE.find(k => 
          k.includeIds?.includes(kursId) || k.id === kursId
        );
        
        if (zielKurs && counts[zielKurs.id] !== undefined && !gezaehlteTN[zielKurs.id].has(t.id)) {
          // Prüfe ob Kurs heute noch aktiv ist
          const kursStart = t.kurs_start ? t.kurs_start.substring(0, 10) : null;
          const kursEnde = t.kurs_ende ? t.kurs_ende.substring(0, 10) : null;
          
          let imZeitraum = true;
          if (kursStart && heute < kursStart) imZeitraum = false;
          if (kursEnde && heute > kursEnde) imZeitraum = false; // Kurs beendet = nicht zählen
          
          if (imZeitraum) {
            counts[zielKurs.id]++;
            gezaehlteTN[zielKurs.id].add(t.id);
          }
        }
        
        // 2. Zusätzliche Kurse prüfen
        if (t.kurse && Array.isArray(t.kurse)) {
          for (const zusatzKurs of t.kurse) {
            const zusatzKursId = zusatzKurs.kurs_id || mapKursToId(zusatzKurs.kurs_name);
            
            // Finde den Haupt-Kurs-Eintrag
            const zielKurs2 = KURSE.find(k => 
              k.includeIds?.includes(zusatzKursId) || k.id === zusatzKursId
            );
            
            if (zielKurs2 && counts[zielKurs2.id] !== undefined && !gezaehlteTN[zielKurs2.id].has(t.id)) {
              // Prüfe ob Kurs heute noch aktiv ist
              const start = zusatzKurs.start_datum ? zusatzKurs.start_datum.substring(0, 10) : 
                           (zusatzKurs.start ? zusatzKurs.start.substring(0, 10) : null);
              const ende = zusatzKurs.end_datum ? zusatzKurs.end_datum.substring(0, 10) : 
                          (zusatzKurs.ende ? zusatzKurs.ende.substring(0, 10) : null);
              
              let imZeitraum = true;
              if (start && heute < start) imZeitraum = false;
              if (ende && heute > ende) imZeitraum = false; // Kurs beendet = nicht zählen
              
              if (imZeitraum) {
                counts[zielKurs2.id]++;
                gezaehlteTN[zielKurs2.id].add(t.id);
              }
            }
          }
        }
      });
      
      setKursTeilnehmerCount(counts);
    } catch (error) {
      console.error('Fehler beim Laden:', error);
    }
  };

  const loadKursData = async (kursId, checkDatum = null) => {
    const aktivDatum = checkDatum || datum;
    const heute = new Date().toISOString().split('T')[0]; // Immer heute für "aktive" Prüfung
    setLoading(true);
    try {
      const response = await axios.get(`${API}/teilnehmer?limit=10000`);
      const alleTeilnehmer = response.data;
      
      // Finde den Kurs und hole alle zugehörigen IDs (für zusammengeführte Kurse)
      const kursConfig = KURSE.find(k => k.id === kursId);
      const alleKursIds = kursConfig?.includeIds || [kursId];
      
      // Teilnehmer im richtigen Kurs UND mit aktivem Kurszeitraum (noch nicht beendet)
      const kursTeilnehmer = alleTeilnehmer.filter(t => {
        // 1. Prüfe Hauptkurs gegen alle zugehörigen IDs
        const mappedId = mapKursToId(t.kurs);
        let istImHauptkurs = alleKursIds.includes(mappedId);
        
        if (istImHauptkurs) {
          // Prüfe Zeitraum des Hauptkurses - Kurs muss HEUTE noch aktiv sein
          const kursStart = t.kurs_start ? t.kurs_start.substring(0, 10) : null;
          const kursEnde = t.kurs_ende ? t.kurs_ende.substring(0, 10) : null;
          
          // Kurs hat noch nicht begonnen
          if (kursStart && heute < kursStart) {
            istImHauptkurs = false;
          }
          // Kurs ist bereits beendet (Enddatum liegt in der Vergangenheit)
          if (kursEnde && heute > kursEnde) {
            istImHauptkurs = false;
          }
        }
        
        // 2. Prüfe zusätzliche Kurse (kurse Array) gegen alle zugehörigen IDs
        let istInZusatzKurs = false;
        if (t.kurse && Array.isArray(t.kurse)) {
          for (const zusatzKurs of t.kurse) {
            const zusatzKursId = zusatzKurs.kurs_id || mapKursToId(zusatzKurs.kurs_name);
            if (alleKursIds.includes(zusatzKursId)) {
              // Prüfe Zeitraum - auch hier gegen HEUTE prüfen
              const start = zusatzKurs.start_datum ? zusatzKurs.start_datum.substring(0, 10) : 
                           (zusatzKurs.start ? zusatzKurs.start.substring(0, 10) : null);
              const ende = zusatzKurs.end_datum ? zusatzKurs.end_datum.substring(0, 10) : 
                          (zusatzKurs.ende ? zusatzKurs.ende.substring(0, 10) : null);
              
              let zeitraumOk = true;
              // Kurs hat noch nicht begonnen
              if (start && heute < start) {
                zeitraumOk = false;
              }
              // Kurs ist bereits beendet
              if (ende && heute > ende) {
                zeitraumOk = false;
              }
              
              if (zeitraumOk) {
                istInZusatzKurs = true;
                break;
              }
            }
          }
        }
        
        return istImHauptkurs || istInZusatzKurs;
      });
      
      setTeilnehmer(kursTeilnehmer);
      await loadAnwesenheit(kursId, aktivDatum, kursTeilnehmer);
      await loadUeEintraege(kursId, aktivDatum, kursTeilnehmer);
    } catch (error) {
      console.error('Fehler:', error);
      toast.error('Fehler beim Laden');
    } finally {
      setLoading(false);
    }
  };

  const loadAnwesenheit = async (kursId, date, tnListe = teilnehmer) => {
    try {
      const { data } = await axios.post(`${API}/anwesenheit-tag/laden`, {
        kurs_id: kursId, datum: date, teilnehmer_ids: tnListe.map(t => t.id)
      });
      const gespeichert = data.doc?.anwesenheit || {};
      const voll = {};
      tnListe.forEach(t => { voll[t.id] = gespeichert[t.id] || 'anwesend'; });
      setAnwesenheit(voll);
      setAnwDetails(data.doc?.details || {});
      setEinwilligungen(data.einwilligungen || {});
      setTagDoc(data.doc?.kurs_id ? data.doc : null);
    } catch (error) {
      setAnwesenheit({});
      setAnwDetails({});
      setTagDoc(null);
    }
  };

  const loadUeEintraege = async (kursId, date, tnListe = teilnehmer) => {
    setUePlan(null);
    try {
      const response = await axios.get(`${API}/klassenbuch/ue-eintraege/${kursId}/${date}`);
      const eintraege = response.data || [];
      const kohorte = kohorteFuer(tnListe, kursId, date);
      const planRes = await axios.get(`${API}/anwesenheit-tag/plan/${kursId}`, {
        params: { von: date, bis: date, kurs_start: kohorte?.start || '', kurs_ende: kohorte?.ende || '' }
      }).catch(() => null);
      const plan = planRes?.data?.tage?.[0];
      if (plan) setUePlan(plan);
      if (eintraege.length === 0 && plan?.thema_plan) {
        setUeEintraege([{ ue: 1, thema: plan.thema_plan, dozent: dozentOption(user?.name), notizen: plan.lernziel_plan || '', vorbefuellt: true }]);
      } else if (eintraege.length > 0) {
        setUeEintraege(eintraege.map(e => ({
          ue: e.ue,
          thema: e.thema || '',
          dozent: e.dozent || '',
          notizen: e.notizen || ''
        })));
      } else {
        setUeEintraege([{ ue: 1, thema: '', dozent: '', notizen: '' }]);
      }
    } catch (error) {
      setUeEintraege([{ ue: 1, thema: '', dozent: '', notizen: '' }]);
    }
  };

  const handleDatumChange = (newDatum) => {
    setDatum(newDatum);
    if (selectedKurs) {
      // Bei Datumswechsel auch Teilnehmer neu laden (wegen Kurszeitraum-Filter)
      loadKursData(selectedKurs.id, newDatum);
    }
  };

  const handleKursClick = (kurs) => {
    setSelectedKurs(kurs);
    loadKursData(kurs.id);
  };

  const handleBack = () => {
    setSelectedKurs(null);
    setTeilnehmer([]);
    setAnwesenheit({});
    setUeEintraege([{ ue: 1, thema: '', dozent: '', notizen: '' }]);
  };

  const setStatus = (teilnehmerId, status) => {
    setAnwesenheit(prev => ({ ...prev, [teilnehmerId]: status }));
  };

  const alleAnwesend = () => {
    const newAnwesenheit = {};
    teilnehmer.forEach(t => {
      newAnwesenheit[t.id] = 'anwesend';
    });
    setAnwesenheit(newAnwesenheit);
  };

  const alleFehlend = () => {
    if (!window.confirm('Alle Teilnehmenden für diesen Tag als „abwesend (unentschuldigt)“ eintragen?')) return;
    setAnwesenheit(Object.fromEntries(teilnehmer.map(t => [t.id, 'fehlend'])));
  };

  const kursWechseln = (id) => {
    const k = KURSE.find(x => x.id === id);
    if (!k) return;
    setSelectedKurs(k);
    loadKursData(k.id, datum);
  };

  // UE-Einträge verwalten
  const addUeEintrag = () => {
    const nextUe = ueEintraege.length > 0 ? Math.max(...ueEintraege.map(e => e.ue)) + 1 : 1;
    setUeEintraege([...ueEintraege, { ue: nextUe, thema: '', dozent: '', notizen: '' }]);
  };

  const removeUeEintrag = (index) => {
    if (ueEintraege.length > 1) {
      setUeEintraege(ueEintraege.filter((_, i) => i !== index));
    }
  };

  const updateUeEintrag = (index, field, value) => {
    const updated = [...ueEintraege];
    updated[index][field] = value;
    setUeEintraege(updated);
  };

  const speichern = async (abschliessen = false, korrekturgrund = '') => {
    if (!selectedKurs) return;
    if (abschliessen && !window.confirm('Tag abschließen?\n\nDie Unterschriften der anwesenden Teilnehmenden (mit Einwilligung) werden übertragen. Danach kann nur noch die Verwaltung korrigieren.')) return;
    const ids = teilnehmer.map(t => t.id);
    const anw = Object.fromEntries(ids.map(id => [id, anwesenheit[id] || 'anwesend']));
    const details = Object.fromEntries(ids.filter(id => anw[id] === 'teilweise').map(id => [id, anwDetails[id] || {}]));
    const namen = Object.fromEntries(teilnehmer.map(t => [t.id, `${t.nachname}, ${t.vorname}`]));
    try {
      const { data } = await axios.post(`${API}/anwesenheit-tag/speichern`, {
        kurs_id: selectedKurs.id, datum, anwesenheit: anw, details, namen, abschliessen, korrekturgrund, ue_tag: uePlan?.ue_plan || null,
        ue_eintraege: ueEintraege.filter(e => e.thema)
      });
      setReloadKey(k => k + 1);
      toast.success(data.abgeschlossen ? `Gespeichert – Tag abgeschlossen (${data.signaturen} Unterschriften übertragen)` : 'Gespeichert');
      await loadAnwesenheit(selectedKurs.id, datum);
    } catch (error) {
      const detail = error.response?.data?.detail || 'Fehler beim Speichern';
      if (detail.startsWith('KORREKTURGRUND')) {
        const grund = window.prompt('Der Tag ist abgeschlossen. Grund der Korrektur (wird protokolliert):');
        if (grund && grund.trim()) return speichern(abschliessen, grund.trim());
        return;
      }
      toast.error(detail);
    }
  };

  // Statistik
  const stats = {
    anwesend: Object.values(anwesenheit).filter(s => s === 'anwesend').length,
    entschuldigt: Object.values(anwesenheit).filter(s => s === 'entschuldigt').length,
    fehlend: Object.values(anwesenheit).filter(s => s === 'fehlend').length,
    teilweise: Object.values(anwesenheit).filter(s => s === 'teilweise').length,
    gesamt_ue: ueEintraege.length
  };

  // Vorlagen laden
  const loadVorlagen = async () => {
    try {
      const res = await axios.get(`${API}/stundenplan-vorlagen`);
      setVorlagen(res.data);
      // Themen für Dropdown speichern
      if (res.data?.pfh) {
        setThemenVorlage(res.data.pfh);
      }
    } catch (error) {
      console.error('Fehler beim Laden der Vorlagen:', error);
    }
  };

  // Beim Laden der Seite Vorlagen laden
  useEffect(() => {
    loadVorlagen();
  }, []);

  // Thema aus Vorlage auswählen
  const handleThemaSelect = (tagNr) => {
    const thema = themenVorlage.find(t => t.tag === parseInt(tagNr));
    if (thema) {
      setSelectedThema(thema);
      // Ersten UE-Eintrag mit Thema befüllen
      const updatedEintraege = [...ueEintraege];
      updatedEintraege[0] = {
        ...updatedEintraege[0],
        thema: thema.thema,
        notizen: `Lernziel: ${thema.lernziel}`
      };
      setUeEintraege(updatedEintraege);
      toast.success(`Thema "${thema.thema}" ausgewählt`);
    }
  };

  // Kurs-Durchgänge für historisches Klassenbuch laden
  const loadKursDurchgaenge = async () => {
    try {
      const res = await axios.get(`${API}/klassenbuch-historie/kurs-durchgaenge`);
      setKursDurchgaenge(res.data || []);
    } catch (error) {
      toast.error('Fehler beim Laden der Kurs-Durchgänge');
    }
  };

  // Klassenbuch für einen Teilnehmer löschen (zurücksetzen) - DEAKTIVIERT AUS SICHERHEITSGRÜNDEN
  const handleDeleteKlassenbuch = async (teilnehmerId, teilnehmerName, kursId = null) => {
    // SICHERHEIT: Diese Funktion ist deaktiviert um Datenverlust zu verhindern!
    toast.error('GESPERRT: Das Löschen von Klassenbuch-Daten ist aus Sicherheitsgründen deaktiviert. Bitte kontaktieren Sie den Administrator.');
    return;
  };

  // NEU: Vorschau laden statt direkt erstellen
  const handleLoadVorschau = async () => {
    if (!selectedDurchgang) {
      toast.error('Bitte wählen Sie einen Kurs-Durchgang');
      return;
    }
    
    if (!historischStartDatum) {
      toast.error('Bitte geben Sie das Startdatum ein');
      return;
    }
    if (!historischEndDatum) {
      toast.error('Bitte geben Sie das Enddatum ein');
      return;
    }
    if (historischStartDatum > historischEndDatum) {
      toast.error('Das Enddatum muss nach dem Startdatum liegen');
      return;
    }
    
    setLoadingVorschau(true);
    try {
      const teilnehmerIds = selectedEinzelTeilnehmer 
        ? [selectedEinzelTeilnehmer.id]
        : selectedDurchgang.teilnehmer.map(t => t.id);
      
      const res = await axios.post(
        `${API}/klassenbuch-historie/vorschau?kurs_id=${selectedKursTyp}&start_datum=${historischStartDatum}&end_datum=${historischEndDatum}&vorlage_id=${selectedVorlageId}&nur_bis_heute=${nurBisHeute}`,
        teilnehmerIds
      );
      
      setVorschauData(res.data);
      setShowHistorischDialog(false);
      setShowVorschauDialog(true);
      toast.success('Vorschau geladen - bitte prüfen Sie die Anwesenheit');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Laden der Vorschau');
    } finally {
      setLoadingVorschau(false);
    }
  };

  // NEU: Anwesenheit in der Vorschau ändern
  const handleChangeAnwesenheit = (teilnehmerIndex, tagIndex, newStatus) => {
    if (!vorschauData) return;
    
    const updatedData = { ...vorschauData };
    updatedData.teilnehmer[teilnehmerIndex].tage[tagIndex].status = newStatus;
    
    // Statistik neu berechnen
    const tage = updatedData.teilnehmer[teilnehmerIndex].tage;
    const anwesend = tage.filter(t => t.status === 'anwesend').length;
    const entschuldigt = tage.filter(t => t.status === 'entschuldigt').length;
    updatedData.teilnehmer[teilnehmerIndex].statistik = {
      gesamt: tage.length,
      anwesend,
      entschuldigt,
      prozent: Math.round((anwesend / tage.length) * 100 * 10) / 10
    };
    
    setVorschauData(updatedData);
  };

  // NEU: Thema in der Vorschau ändern
  const handleChangeThema = (teilnehmerIndex, tagIndex, newThema) => {
    if (!vorschauData) return;
    
    const updatedData = { ...vorschauData };
    updatedData.teilnehmer[teilnehmerIndex].tage[tagIndex].thema = newThema;
    
    setVorschauData(updatedData);
  };

  // NEU: Datum in der Vorschau ändern (nur für diesen Teilnehmer)
  const handleChangeDatum = (teilnehmerIndex, tagIndex, newDatum) => {
    if (!vorschauData) return;
    
    const updatedData = { ...vorschauData };
    const tn = updatedData.teilnehmer[teilnehmerIndex];
    
    if (tn.tage[tagIndex]) {
      tn.tage[tagIndex].datum = newDatum;
      // Formatiere auch das Anzeige-Datum
      const [year, month, day] = newDatum.split('-');
      tn.tage[tagIndex].datum_display = `${day}.${month}.${year}`;
      // Berechne Wochentag
      const date = new Date(newDatum);
      const wochentage = ['So', 'Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa'];
      tn.tage[tagIndex].wochentag = wochentage[date.getDay()];
    }
    
    setVorschauData(updatedData);
  };

  // NEU: Zeile/Tag hinzufügen (nur für diesen Teilnehmer)
  const handleAddTag = (teilnehmerIndex, afterIndex) => {
    if (!vorschauData) return;
    
    const updatedData = { ...vorschauData };
    const tn = updatedData.teilnehmer[teilnehmerIndex];
    const prevTag = tn.tage[afterIndex];
    
    // Berechne nächstes Datum (nächster Werktag)
    let nextDate = new Date(prevTag.datum);
    nextDate.setDate(nextDate.getDate() + 1);
    while (nextDate.getDay() === 0 || nextDate.getDay() === 6) {
      nextDate.setDate(nextDate.getDate() + 1);
    }
    const newDatum = nextDate.toISOString().split('T')[0];
    const wochentage = ['So', 'Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa'];
    
    const newTag = {
      tag: afterIndex + 2,
      datum: newDatum,
      datum_display: `${nextDate.getDate().toString().padStart(2, '0')}.${(nextDate.getMonth() + 1).toString().padStart(2, '0')}.${nextDate.getFullYear()}`,
      wochentag: wochentage[nextDate.getDay()],
      thema: "Neues Thema",
      lernziel: "",
      status: "anwesend"
    };
    
    // Füge nach dem Index ein
    tn.tage.splice(afterIndex + 1, 0, newTag);
    
    // Aktualisiere Tag-Nummern
    tn.tage.forEach((t, i) => {
      t.tag = i + 1;
    });
    
    // Aktualisiere Statistik
    const anwesend = tn.tage.filter(t => t.status === 'anwesend').length;
    const entschuldigt = tn.tage.filter(t => t.status === 'entschuldigt').length;
    tn.statistik = {
      gesamt: tn.tage.length,
      anwesend: anwesend,
      entschuldigt: entschuldigt,
      prozent: Math.round((anwesend / tn.tage.length) * 100 * 10) / 10
    };
    
    setVorschauData(updatedData);
    toast.success(`Tag ${afterIndex + 2} hinzugefügt`);
  };

  // NEU: Zeile/Tag löschen (nur für diesen Teilnehmer)
  const handleDeleteTag = (teilnehmerIndex, tagIndex) => {
    if (!vorschauData) return;
    
    const updatedData = { ...vorschauData };
    const tn = updatedData.teilnehmer[teilnehmerIndex];
    
    if (tn.tage.length <= 1) {
      toast.error('Mindestens 1 Tag muss vorhanden sein');
      return;
    }
    
    // Lösche den Tag
    tn.tage.splice(tagIndex, 1);
    
    // Aktualisiere Tag-Nummern
    tn.tage.forEach((t, i) => {
      t.tag = i + 1;
    });
    
    // Aktualisiere Statistik
    const anwesend = tn.tage.filter(t => t.status === 'anwesend').length;
    const entschuldigt = tn.tage.filter(t => t.status === 'entschuldigt').length;
    tn.statistik = {
      gesamt: tn.tage.length,
      anwesend: anwesend,
      entschuldigt: entschuldigt,
      prozent: tn.tage.length > 0 ? Math.round((anwesend / tn.tage.length) * 100 * 10) / 10 : 0
    };
    
    setVorschauData(updatedData);
    toast.success(`Tag ${tagIndex + 1} gelöscht`);
  };

  // NEU: Klassenbuch mit den geprüften Daten speichern
  const handleSaveWithAnwesenheit = async (ueberschreiben = false) => {
    console.log('=== handleSaveWithAnwesenheit START ===');
    
    if (!vorschauData) {
      console.error('FEHLER: vorschauData ist null/undefined');
      toast.error('Keine Vorschau-Daten vorhanden');
      return;
    }
    
    console.log('kurs_id:', vorschauData.kurs_id);
    console.log('vorlage_id:', vorschauData.vorlage_id);
    console.log('teilnehmer count:', vorschauData.teilnehmer?.length);
    
    setCreatingHistorisch(true);
    try {
      // Bereinige die Teilnehmer-Daten von React-Referenzen
      const cleanTeilnehmer = vorschauData.teilnehmer?.map(tn => ({
        teilnehmer_id: tn.teilnehmer_id,
        teilnehmer_name: tn.teilnehmer_name,
        tage: tn.tage?.map(tag => ({
          tag: tag.tag,
          datum: tag.datum,
          datum_display: tag.datum_display,
          wochentag: tag.wochentag,
          thema: tag.thema,
          lernziel: tag.lernziel,
          status: tag.status,
          ue: tag.ue
        }))
      })) || [];
      
      const requestData = {
        kurs_id: vorschauData.kurs_id,
        vorlage_id: vorschauData.vorlage_id,
        dozent: historischDozent,
        teilnehmer: cleanTeilnehmer,
        generate_pdfs: downloadPdfsAfterCreate,
        save_to_teilnehmer: savePdfsToTeilnehmer,
        ueberschreiben: ueberschreiben
      };
      console.log('Sende Request an API...');
      
      const res = await axios.post(`${API}/klassenbuch-historie/speichern-mit-anwesenheit`, requestData);
      
      console.log('Response erhalten:', res.data?.success, res.data?.message);
      
      // Wenn bereits existiert und nicht überschrieben werden soll
      if (res.data.success === false && res.data.existing) {
        const choice = window.prompt(
          `Klassenbuch existiert bereits (${res.data.existing} Einträge).\n\n` +
          `Optionen:\n` +
          `  M = Nur die AKTUELL ausgewählten Teilnehmer HINZUFÜGEN (bestehende Einträge bleiben)\n` +
          `  Ü = ALLES ÜBERSCHREIBEN (löscht alle bestehenden TN und Anwesenheiten!)\n` +
          `  Abbrechen = keine Änderung\n\n` +
          `Bitte "M" oder "Ü" eingeben:`,
          "M"
        );
        if (!choice) {
          setCreatingHistorisch(false);
          return;
        }
        const c = (choice || "").trim().toUpperCase();
        if (c === "M" || c === "MERGE") {
          // Merge-only: nur diese TN hinzufügen
          const mergeData = { ...requestData, merge_only: true, ueberschreiben: false };
          const mergeRes = await axios.post(`${API}/klassenbuch-historie/speichern-mit-anwesenheit`, mergeData);
          toast.success(`Hinzugefügt! ${mergeRes.data.klassenbuch_eintraege || 0} Tage x ${mergeRes.data.teilnehmer || 0} TN gespeichert (bestehende Daten unangetastet)`);
          setShowVorschauDialog(false);
          setVorschauData(null);
          setSelectedDurchgang(null);
          setSelectedEinzelTeilnehmer(null);
          setHistorischStartDatum('');
          setHistorischEndDatum('');
          loadTeilnehmerCounts();
          setCreatingHistorisch(false);
          return;
        }
        if (c === "Ü" || c === "U" || c === "OVERWRITE" || c === "UEBERSCHREIBEN") {
          const confirmed = window.confirm(
            `ACHTUNG: Alle ${res.data.existing} bestehenden Klassenbuch-Einträge werden GELÖSCHT!\n\n` +
            `Sind Sie sicher?`
          );
          if (confirmed) {
            await handleSaveWithAnwesenheit(true);
          } else {
            setCreatingHistorisch(false);
          }
          return;
        }
        setCreatingHistorisch(false);
        return;
      }
      
      toast.success(`Klassenbuch gespeichert! ${res.data.klassenbuch_eintraege} Tage für ${res.data.teilnehmer} Teilnehmer`);
      if (res.data.pdfs_generated === "im_hintergrund") {
        toast.info("PDFs werden im Hintergrund erstellt — bitte einen Moment Geduld bis sie in den Akten erscheinen.");
      }
      
      // PDFs herunterladen wenn gewünscht
      if (downloadPdfsAfterCreate && vorschauData.teilnehmer) {
        toast.info('Lade PDFs herunter...');
        
        // Safari-Erkennung
        const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
        
        for (const tn of vorschauData.teilnehmer) {
          try {
            // Verwende die kurs_id aus der Vorschau für korrekten PDF-Export
            const pdfUrl = `${API}/klassenbuch-neu/teilnehmer/${tn.teilnehmer_id}/stundenplan-pdf?kurs_id=${vorschauData.kurs_id}`;
            const filename = `Anwesenheit_${tn.teilnehmer_name.replace(/\s+/g, '_')}.pdf`;
            
            if (isSafari) {
              // Für Safari: Öffne PDF in neuem Tab (Benutzer kann dann speichern)
              const blobRes = await axios.get(pdfUrl, { responseType: 'blob' });
              window.open(window.URL.createObjectURL(blobRes.data), '_blank');
              toast.info(`PDF für ${tn.teilnehmer_name} wird in neuem Tab geöffnet - bitte manuell speichern (Cmd+S)`);
            } else {
              // Für andere Browser: Normaler Download
              const pdfRes = await axios.get(pdfUrl, { responseType: 'blob' });
              
              if (!pdfRes.data || pdfRes.data.size === 0) {
                console.error('PDF ist leer für:', tn.teilnehmer_name);
                toast.error(`PDF für ${tn.teilnehmer_name} ist leer`);
                continue;
              }
              
              const blob = new Blob([pdfRes.data], { type: 'application/pdf' });
              const url = window.URL.createObjectURL(blob);
              
              const link = document.createElement('a');
              link.href = url;
              link.download = filename;
              link.style.display = 'none';
              document.body.appendChild(link);
              
              await new Promise(resolve => setTimeout(resolve, 100));
              link.click();
              
              setTimeout(() => {
                document.body.removeChild(link);
                window.URL.revokeObjectURL(url);
              }, 200);
              
              toast.success(`PDF für ${tn.teilnehmer_name} heruntergeladen`);
            }
            
            // Pause zwischen Downloads
            await new Promise(resolve => setTimeout(resolve, 500));
          } catch (err) {
            console.error('PDF Download Fehler:', err);
            toast.error(`Fehler beim Download für ${tn.teilnehmer_name}: ${err.message}`);
          }
        }
        toast.success(isSafari ? 'PDFs in neuen Tabs geöffnet!' : 'Alle PDFs heruntergeladen!');
      }
      
      setShowVorschauDialog(false);
      setVorschauData(null);
      setSelectedDurchgang(null);
      setSelectedEinzelTeilnehmer(null);
      setHistorischStartDatum('');
      setHistorischEndDatum('');
      loadTeilnehmerCounts();
    } catch (error) {
      console.error('=== FEHLER beim Speichern ===');
      console.error('Error object:', error);
      console.error('Error message:', error.message);
      console.error('Error response:', error.response);
      console.error('Error response data:', error.response?.data);
      toast.error(error.response?.data?.detail || error.message || 'Fehler beim Speichern');
    } finally {
      setCreatingHistorisch(false);
    }
  };

  // Historisches Klassenbuch erstellen (Fallback/Legacy)
  const handleCreateHistorisch = async () => {
    // Leite jetzt zur Vorschau weiter
    await handleLoadVorschau();
  };

  // NEU: Einzelnen Teilnehmer Klassenbuch öffnen
  const handleOpenTeilnehmerKlassenbuch = async (teilnehmer) => {
    console.log('Öffne Klassenbuch für:', teilnehmer);
    console.log('ALLE Teilnehmer-Felder:', JSON.stringify(teilnehmer, null, 2));
    
    // Dialog sofort öffnen
    setSelectedTeilnehmerForEdit(teilnehmer);
    setShowTeilnehmerDialog(true);
    setLoadingTeilnehmerKlassenbuch(true);
    setTeilnehmerAnwesenheitEdit([]); // Reset
    
    try {
      // Versuche verschiedene Kurs-IDs (Hauptkurs und aus kurse-Array)
      const kursId = selectedKurs?.id;
      const alleKursIds = selectedKurs?.includeIds || [kursId];
      
      console.log('Kurs ID:', kursId, 'Alle IDs:', alleKursIds);
      
      if (!kursId) {
        toast.error('Kein Kurs ausgewählt');
        setLoadingTeilnehmerKlassenbuch(false);
        return;
      }
      
      // WICHTIG: Hole die Kursdaten aus ALLEN möglichen Feldern
      // Priorisierung: kurse-Array > bg_start/bg_ende > kurs_start/kurs_ende > massnahme_start/massnahme_ende
      let kursStartDatum = '';
      let kursEndeDatum = '';
      
      // 1. Prüfe kurse-Array (hat höchste Priorität für spezifische Kursdaten)
      if (teilnehmer.kurse && Array.isArray(teilnehmer.kurse) && teilnehmer.kurse.length > 0) {
        // Suche nach dem passenden Kurs im kurse-Array
        const passenderKurs = teilnehmer.kurse.find(k => 
          alleKursIds.some(id => 
            k.kurs_id === id || 
            k.kurs_name?.toLowerCase().includes(selectedKurs?.name?.toLowerCase()) ||
            selectedKurs?.name?.toLowerCase().includes(k.kurs_name?.toLowerCase())
          )
        ) || teilnehmer.kurse[0]; // Fallback zum ersten Kurs
        
        if (passenderKurs) {
          kursStartDatum = (passenderKurs.start || passenderKurs.start_datum || passenderKurs.kurs_start || '')?.substring?.(0, 10) || '';
          kursEndeDatum = (passenderKurs.ende || passenderKurs.end_datum || passenderKurs.kurs_ende || '')?.substring?.(0, 10) || '';
          console.log('Gefundener Kurs im kurse-Array:', passenderKurs);
        }
      }
      
      // 2. Fallback: BG-spezifische Felder
      if (!kursStartDatum && teilnehmer.bg_start) {
        kursStartDatum = teilnehmer.bg_start?.substring?.(0, 10) || '';
        console.log('Verwende bg_start:', kursStartDatum);
      }
      if (!kursEndeDatum && teilnehmer.bg_ende) {
        kursEndeDatum = teilnehmer.bg_ende?.substring?.(0, 10) || '';
      }
      
      // 3. Fallback: Standard kurs_start/kurs_ende Felder
      if (!kursStartDatum && teilnehmer.kurs_start) {
        kursStartDatum = teilnehmer.kurs_start?.substring?.(0, 10) || '';
        console.log('Verwende kurs_start:', kursStartDatum);
      }
      if (!kursEndeDatum && teilnehmer.kurs_ende) {
        kursEndeDatum = teilnehmer.kurs_ende?.substring?.(0, 10) || '';
      }
      
      // 4. Fallback: massnahme_start/massnahme_ende
      if (!kursStartDatum && teilnehmer.massnahme_start) {
        kursStartDatum = teilnehmer.massnahme_start?.substring?.(0, 10) || '';
        console.log('Verwende massnahme_start:', kursStartDatum);
      }
      if (!kursEndeDatum && teilnehmer.massnahme_ende) {
        kursEndeDatum = teilnehmer.massnahme_ende?.substring?.(0, 10) || '';
      }
      
      // 5. Fallback: Andere mögliche Felder
      if (!kursStartDatum && teilnehmer.start) {
        kursStartDatum = teilnehmer.start?.substring?.(0, 10) || '';
        console.log('Verwende start:', kursStartDatum);
      }
      if (!kursStartDatum && teilnehmer.startdatum) {
        kursStartDatum = teilnehmer.startdatum?.substring?.(0, 10) || '';
        console.log('Verwende startdatum:', kursStartDatum);
      }
      
      console.log('FINALE Kursdaten - Start:', kursStartDatum, 'Ende:', kursEndeDatum);
      
      // WICHTIG: Wenn wir Kursdaten haben, generiere die Tage IMMER basierend auf den Teilnehmer-Kursdaten
      // Die alten Klassenbuch-Einträge werden nur für die Anwesenheit verwendet
      let klassenbuchTage = [];
      let anwesenheitData = [];
      
      // Lade existierende Anwesenheitsdaten (für Status-Übernahme)
      for (const kid of alleKursIds) {
        try {
          const anwRes = await axios.get(`${API}/anwesenheit?teilnehmer_id=${teilnehmer.id}&kurs_id=${kid}`);
          if (anwRes.data && anwRes.data.length > 0) {
            anwesenheitData = [...anwesenheitData, ...anwRes.data];
          }
        } catch (e) {}
      }
      console.log('Anwesenheit geladen:', anwesenheitData.length);
      
      // NEUE LOGIK: Generiere IMMER basierend auf Teilnehmer-Kursdaten
      if (kursStartDatum) {
        console.log('Generiere Klassenbuch basierend auf Teilnehmer-Kursdaten');
        
        // Bestimme Vorlage basierend auf Kurstyp
        const istLG = selectedKurs?.name?.toLowerCase().includes('lg') || 
                      selectedKurs?.name?.toLowerCase().includes('lehrgang') ||
                      selectedKurs?.name?.toLowerCase().includes('behandlungspflege');
        const vorlageId = istLG ? 'lg' : 'pfh';
        
        try {
          // Hole Generierung vom Backend mit den KORREKTEN Kursdaten
          let url = `${API}/klassenbuch-generiere-tage?start_datum=${kursStartDatum}&vorlage_id=${vorlageId}`;
          if (kursEndeDatum) {
            url += `&end_datum=${kursEndeDatum}`;
          }
          
          const genRes = await axios.get(url);
          
          if (genRes.data && genRes.data.tage && genRes.data.tage.length > 0) {
            klassenbuchTage = genRes.data.tage.map((tag) => {
              // Prüfe, ob es bereits Anwesenheitsdaten für dieses Datum gibt
              const existierendeAnwesenheit = anwesenheitData.find(a => a.datum === tag.datum);
              return {
                datum: tag.datum,
                datum_display: tag.datum_display,
                thema: tag.thema || '',
                lernziel: tag.lernziel || '',
                ue: tag.ue || 8,
                tag_im_kurs: tag.tag,
                wochentag: tag.wochentag,
                dozent: 'Dickie (MD)',
                status: existierendeAnwesenheit?.status || 'anwesend'
              };
            });
            console.log('Klassenbuch generiert mit Teilnehmer-Kursdaten:', klassenbuchTage.length, 'Tage');
            toast.success(`Klassenbuch: ${klassenbuchTage.length} Unterrichtstage (${kursStartDatum} bis ${genRes.data.end_datum})`);
          }
        } catch (genError) {
          console.error('Fehler beim Generieren:', genError);
          toast.error('Klassenbuch konnte nicht generiert werden. Bitte Kursdaten prüfen.');
        }
      } else {
        // Kein Startdatum vorhanden - versuche alte Klassenbuch-Einträge zu laden
        toast.warning('Kein Kurs-Startdatum in der Teilnehmerakte gefunden!');
        console.log('WARNUNG: Kein Kurs-Startdatum gefunden - lade alte Klassenbuch-Einträge');
        
        for (const kid of alleKursIds) {
          try {
            const kbRes = await axios.get(`${API}/klassenbuch?kurs_id=${kid}`);
            if (kbRes.data && kbRes.data.length > 0) {
              klassenbuchTage = [...klassenbuchTage, ...kbRes.data];
              console.log(`Klassenbuch-Tage für ${kid}:`, kbRes.data.length);
            }
          } catch (e) {
            console.log(`Keine Daten für kurs_id ${kid}`);
          }
        }
      }
      
      // Erstelle kombinierte Liste
      const kombiniert = klassenbuchTage.map(tag => {
        const anw = anwesenheitData.find(a => a.datum === tag.datum);
        return {
          ...tag,
          datum_display: tag.datum_display || (tag.datum ? new Date(tag.datum).toLocaleDateString('de-DE') : '-'),
          status: tag.status || anw?.status || 'anwesend'
        };
      }).sort((a, b) => (a.datum || '').localeCompare(b.datum || ''));
      
      // Duplikate entfernen (nach Datum)
      const uniqueTage = kombiniert.filter((tag, index, self) => 
        index === self.findIndex(t => t.datum === tag.datum)
      );
      
      console.log('Finale Liste (unique):', uniqueTage.length);
      setTeilnehmerAnwesenheitEdit(uniqueTage);
    } catch (error) {
      console.error('Fehler beim Laden:', error);
      toast.error('Fehler beim Laden: ' + (error.response?.data?.detail || error.message));
    } finally {
      setLoadingTeilnehmerKlassenbuch(false);
    }
  };

  // NEU: Anwesenheit für einzelnen Teilnehmer ändern
  const handleChangeTeilnehmerAnwesenheit = (tagIndex, newStatus) => {
    const updated = [...teilnehmerAnwesenheitEdit];
    updated[tagIndex].status = newStatus;
    setTeilnehmerAnwesenheitEdit(updated);
  };

  // NEU: Beliebiges Feld im Klassenbuch-Eintrag ändern (Datum, Thema, etc.)
  const handleChangeTeilnehmerEintrag = (tagIndex, field, value) => {
    const updated = [...teilnehmerAnwesenheitEdit];
    updated[tagIndex][field] = value;
    // Bei Datum auch datum_display aktualisieren
    if (field === 'datum' && value) {
      try {
        updated[tagIndex].datum_display = new Date(value).toLocaleDateString('de-DE');
      } catch (e) {
        updated[tagIndex].datum_display = value;
      }
    }
    setTeilnehmerAnwesenheitEdit(updated);
  };

  // NEU: Neuen Tag zum Klassenbuch hinzufügen
  const handleAddTeilnehmerTag = () => {
    const neuerTag = {
      datum: new Date().toISOString().split('T')[0],
      datum_display: new Date().toLocaleDateString('de-DE'),
      thema: '',
      tag_im_kurs: teilnehmerAnwesenheitEdit.length + 1,
      status: 'anwesend',
      ue: 8
    };
    setTeilnehmerAnwesenheitEdit([...teilnehmerAnwesenheitEdit, neuerTag]);
  };

  // NEU: Tag aus Klassenbuch entfernen
  const handleRemoveTeilnehmerTag = (tagIndex) => {
    if (teilnehmerAnwesenheitEdit.length <= 1) {
      toast.error('Mindestens ein Eintrag muss vorhanden sein');
      return;
    }
    const updated = teilnehmerAnwesenheitEdit.filter((_, idx) => idx !== tagIndex);
    // Tag-Nummern neu berechnen
    updated.forEach((tag, idx) => {
      tag.tag_im_kurs = idx + 1;
    });
    setTeilnehmerAnwesenheitEdit(updated);
  };

  // NEU: Anwesenheit für einzelnen Teilnehmer speichern
  const handleSaveTeilnehmerAnwesenheit = async () => {
    if (!selectedTeilnehmerForEdit || !selectedKurs) return;
    
    try {
      const kursId = selectedKurs.id;
      
      // Speichere jeden Klassenbuch-Eintrag (Datum, Thema, UE) UND Anwesenheit
      for (const tag of teilnehmerAnwesenheitEdit) {
        // 1. Klassenbuch-Eintrag speichern/aktualisieren (Thema, UE, etc.)
        await axios.post(`${API}/klassenbuch/eintrag`, {
          kurs_id: kursId,
          datum: tag.datum,
          thema: tag.thema || '',
          ue: tag.ue || 8,
          dozent: tag.dozent || 'Dickie (MD)',
          tag_im_kurs: tag.tag_im_kurs
        });
        
        // 2. Anwesenheit für diesen Teilnehmer speichern
        await axios.post(`${API}/anwesenheit`, {
          teilnehmer_id: selectedTeilnehmerForEdit.id,
          kurs_id: kursId,
          datum: tag.datum,
          status: tag.status
        });
      }
      
      toast.success('Klassenbuch gespeichert!');
      
      // Aktualisiere die Anwesenheit in der Hauptansicht
      const newAnwesenheit = { ...anwesenheit };
      teilnehmerAnwesenheitEdit.forEach(tag => {
        // Nur für das aktuelle Datum aktualisieren
        if (tag.datum === datum) {
          newAnwesenheit[selectedTeilnehmerForEdit.id] = tag.status;
        }
      });
      setAnwesenheit(newAnwesenheit);
    } catch (error) {
      console.error('Speicherfehler:', error);
      toast.error('Fehler beim Speichern: ' + (error.response?.data?.detail || error.message));
    }
  };

  // NEU: PDF für einzelnen Teilnehmer herunterladen
  const handleDownloadTeilnehmerPdf = async () => {
    if (!selectedTeilnehmerForEdit || teilnehmerAnwesenheitEdit.length === 0) {
      toast.error('Keine Klassenbuch-Daten zum Exportieren');
      return;
    }
    
    try {
      toast.info('Erstelle PDF...');
      
      // Sende die aktuellen Daten aus dem State an den neuen POST-Endpoint
      const response = await axios.post(
        `${API}/klassenbuch-neu/pdf-from-data`,
        {
          teilnehmer_name: `${selectedTeilnehmerForEdit.vorname} ${selectedTeilnehmerForEdit.nachname}`,
          teilnehmer_vorname: selectedTeilnehmerForEdit.vorname || '',
          teilnehmer_nachname: selectedTeilnehmerForEdit.nachname || '',
          kurs_name: selectedKurs?.name || 'Kurs',
          kurs_start: selectedTeilnehmerForEdit.kurs_start || '',
          tage: teilnehmerAnwesenheitEdit
        },
        { responseType: 'blob' }
      );
      
      const filename = `Klassenbuch_${selectedTeilnehmerForEdit.nachname}_${selectedTeilnehmerForEdit.vorname}.pdf`;
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('PDF heruntergeladen!');
    } catch (error) {
      console.error('PDF-Fehler:', error);
      toast.error(error.response?.data?.detail || 'Fehler beim Erstellen des PDFs');
    }
  };

  // Import aus Vorlage
  const handleImport = async () => {
    if (!selectedKurs || !importStartDatum) {
      toast.error('Bitte Startdatum eingeben');
      return;
    }
    
    setImporting(true);
    try {
      // Kurs-ID generieren (z.B. "pfh_20250113")
      const kursPrefix = selectedKurs.id.startsWith('pflegehilfskraft') ? 'pfh' : 
                         selectedKurs.id.includes('lg1') ? 'lg1lg2' : selectedKurs.id;
      const kursId = `${kursPrefix}_${importStartDatum.replace(/-/g, '')}`;
      
      const res = await axios.post(`${API}/klassenbuch/import-vorlage/${kursId}?vorlage_id=pfh&start_datum=${importStartDatum}&dozent=`);
      
      toast.success(`${res.data.tage_erstellt} Tage importiert!`);
      setShowImportDialog(false);
      loadKursData(selectedKurs.id, datum);
      // Direkt Dozenten-Zuweisungs-Modal öffnen
      try {
        const dz = await axios.get(`${API}/klassenbuch-neu/dozenten-liste`);
        setVerfuegbareDozenten(dz.data.dozenten || []);
      } catch (e) {
        setVerfuegbareDozenten([{ kuerzel: 'JM', name: 'Julia Müller' }, { kuerzel: 'AL', name: 'Annika Lorke' }]);
      }
      setDozentenZuKurs({ kurs_id: kursId, tage_total: res.data.tage_erstellt, kurs_name: selectedKurs.name });
      setBulkZuweisungen([{ tag_von: 1, tag_bis: res.data.tage_erstellt, dozent: 'MD' }]);
      setShowDozentenDialog(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Import');
    } finally {
      setImporting(false);
    }
  };

  // ANSICHT 1: Kurs-Übersicht
  if (!selectedKurs) {
    return (
      <div className="p-6 max-w-6xl mx-auto">
        <div className="mb-6 flex justify-between items-start">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <BookOpen className="h-6 w-6" />
              Klassenbuch
            </h1>
            <p className="text-gray-500">Wählen Sie einen Kurs aus</p>
          </div>
          
          {/* Button für historisches Klassenbuch - AZAV Audit */}
          <Button 
            variant="outline" 
            className="bg-amber-50 border-amber-300 text-amber-700 hover:bg-amber-100"
            onClick={() => {
              loadKursDurchgaenge();
              setShowHistorischDialog(true);
            }}
            data-testid="historisch-klassenbuch-btn"
          >
            <Calendar className="h-4 w-4 mr-2" />
            Historisches Klassenbuch erstellen
          </Button>
        </div>

        {/* Dialog für historisches Klassenbuch */}
        <Dialog open={showHistorischDialog} onOpenChange={setShowHistorischDialog}>
          <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Calendar className="h-5 w-5 text-amber-600" />
                Historisches Klassenbuch erstellen (AZAV Audit)
              </DialogTitle>
            </DialogHeader>
            
            <div className="space-y-4 py-4">
              <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm">
                <p className="font-medium text-amber-700">AZAV Audit Dokumentation</p>
                <p className="text-amber-600 mt-1">
                  Erstellt rückwirkend ein vollständiges Klassenbuch inkl. Anwesenheitsliste für einen vergangenen Kursdurchgang.
                  Die Anwesenheit wird realistisch generiert (2-3 entschuldigte Fehltage pro Teilnehmer).
                </p>
              </div>
              
              <div>
                <label className="text-sm font-medium mb-2 block">Teilnehmer suchen</label>
                <Input 
                  placeholder="Name eingeben zum Filtern..."
                  value={searchTeilnehmer}
                  onChange={(e) => setSearchTeilnehmer(e.target.value)}
                  className="mb-2"
                />
              </div>
              
              <div>
                <label className="text-sm font-medium mb-2 block">Kurs-Durchgang auswählen *</label>
                {kursDurchgaenge.length === 0 ? (
                  <div className="p-4 bg-gray-50 rounded-lg text-center text-gray-500">
                    Lade Kurs-Durchgänge...
                  </div>
                ) : (
                  <Select 
                    onValueChange={(val) => {
                      const durchgang = kursDurchgaenge.find(d => d.id === val);
                      setSelectedDurchgang(durchgang);
                      setSelectedEinzelTeilnehmer(null); // Reset einzelner Teilnehmer bei neuem Durchgang
                      
                      // VERBESSERT: Suche nach den besten verfügbaren Start- und Enddaten
                      // Durchsuche alle Teilnehmer nach gültigen Daten, nicht nur den ersten
                      let vorgeschlagenStartDatum = '';
                      let vorgeschlagenEndDatum = '';
                      
                      if (durchgang?.teilnehmer && durchgang.teilnehmer.length > 0) {
                        // Sammle alle gültigen Startdaten und nimm das früheste
                        const gueltigeStartDaten = durchgang.teilnehmer
                          .map(t => t.kurs_start)
                          .filter(d => d && d.length >= 10 && d !== 'null' && d !== 'undefined')
                          .map(d => d.substring(0, 10));
                        
                        // Sammle alle gültigen Enddaten und nimm das späteste
                        const gueltigeEndDaten = durchgang.teilnehmer
                          .map(t => t.kurs_ende)
                          .filter(d => d && d.length >= 10 && d !== 'null' && d !== 'undefined')
                          .map(d => d.substring(0, 10));
                        
                        if (gueltigeStartDaten.length > 0) {
                          // Nimm das früheste Startdatum
                          gueltigeStartDaten.sort();
                          vorgeschlagenStartDatum = gueltigeStartDaten[0];
                        }
                        
                        if (gueltigeEndDaten.length > 0) {
                          // Nimm das späteste Enddatum
                          gueltigeEndDaten.sort();
                          vorgeschlagenEndDatum = gueltigeEndDaten[gueltigeEndDaten.length - 1];
                        }
                      }
                      
                      // Fallback auf durchgang.start_datum wenn keine Teilnehmerdaten
                      if (!vorgeschlagenStartDatum) {
                        vorgeschlagenStartDatum = durchgang?.start_datum || '';
                      }
                      
                      setHistorischStartDatum(vorgeschlagenStartDatum);
                      setHistorischEndDatum(vorgeschlagenEndDatum);
                      
                      // NEU: Kurstyp automatisch basierend auf dem Kursnamen setzen
                      if (durchgang?.kurs) {
                        const kursId = mapKursToId(durchgang.kurs);
                        if (kursId) {
                          setSelectedKursTyp(kursId);
                          // Auch passende Vorlage setzen
                          if (kursId === 'gerontopsychiatrie') {
                            setSelectedVorlageId('geronto');
                          } else if (kursId === 'pflegedienstleitung') {
                            setSelectedVorlageId('pdl');
                          } else if (kursId.includes('lg1') || kursId.includes('lg2') || kursId.includes('behandlungspflege')) {
                            setSelectedVorlageId('lg');
                          } else {
                            setSelectedVorlageId('pfh');
                          }
                        }
                      }
                    }}
                    data-testid="kurs-durchgang-select"
                  >
                    <SelectTrigger className="w-full">
                      <SelectValue placeholder="Wählen Sie einen vergangenen Kurs..." />
                    </SelectTrigger>
                    <SelectContent className="max-h-[300px]">
                      {kursDurchgaenge
                        .filter(d => {
                          if (!searchTeilnehmer) return true;
                          const search = searchTeilnehmer.toLowerCase();
                          // Suche in Teilnehmernamen
                          const hasMatchingTeilnehmer = d.teilnehmer.some(t => 
                            t.name?.toLowerCase().includes(search)
                          );
                          // Suche auch im Kurs-Label
                          const labelMatch = d.label?.toLowerCase().includes(search);
                          return hasMatchingTeilnehmer || labelMatch;
                        })
                        .map((d) => (
                          <SelectItem key={d.id} value={d.id}>
                            <div className="flex flex-col">
                              <div className="flex items-center gap-2">
                                <span className="font-medium">{d.label}</span>
                                <span className="text-gray-500 text-sm">({d.teilnehmer_count} TN)</span>
                              </div>
                              {searchTeilnehmer && (
                                <span className="text-xs text-gray-400">
                                  {d.teilnehmer.filter(t => t.name?.toLowerCase().includes(searchTeilnehmer.toLowerCase())).map(t => t.name).join(', ')}
                                </span>
                              )}
                            </div>
                          </SelectItem>
                        ))}
                    </SelectContent>
                  </Select>
                )}
                {searchTeilnehmer && kursDurchgaenge.filter(d => {
                  const search = searchTeilnehmer.toLowerCase();
                  return d.teilnehmer.some(t => t.name?.toLowerCase().includes(search)) || d.label?.toLowerCase().includes(search);
                }).length === 0 && (
                  <p className="text-sm text-orange-600 mt-2">Kein Kurs-Durchgang mit "{searchTeilnehmer}" gefunden</p>
                )}
              </div>
              
              {selectedDurchgang && (
                <>
                  <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                    <p className="font-medium text-blue-700 mb-2">
                      Teilnehmer auswählen ({selectedDurchgang.teilnehmer_count}):
                    </p>
                    <p className="text-xs text-blue-600 mb-2">
                      Klicken Sie auf einen Teilnehmer für dessen PDF.
                    </p>
                    <div className="max-h-[150px] overflow-y-auto space-y-1">
                      {/* Option: Alle Teilnehmer */}
                      <div 
                        onClick={() => setSelectedEinzelTeilnehmer(null)}
                        className={`text-sm flex items-center gap-2 p-2 rounded cursor-pointer transition-colors ${
                          selectedEinzelTeilnehmer === null 
                            ? 'bg-blue-200 text-blue-800 font-medium' 
                            : 'hover:bg-blue-100 text-blue-600'
                        }`}
                      >
                        <Users className="h-3 w-3" />
                        Alle Teilnehmer ({selectedDurchgang.teilnehmer_count})
                      </div>
                      {/* Einzelne Teilnehmer */}
                      {selectedDurchgang.teilnehmer.map((t, idx) => (
                        <div 
                          key={t.id || idx} 
                          className={`text-sm flex items-center gap-2 p-2 rounded transition-colors ${
                            selectedEinzelTeilnehmer?.id === t.id 
                              ? 'bg-green-200 text-green-800 font-medium' 
                              : 'hover:bg-blue-100 text-blue-600'
                          }`}
                        >
                          <div 
                            onClick={() => {
                              setSelectedEinzelTeilnehmer(t);
                              // Wenn ein einzelner Teilnehmer ausgewählt wird, setze dessen spezifische Kursdaten
                              if (t.kurs_start && t.kurs_start.length >= 10) {
                                setHistorischStartDatum(t.kurs_start.substring(0, 10));
                              }
                              if (t.kurs_ende && t.kurs_ende.length >= 10) {
                                setHistorischEndDatum(t.kurs_ende.substring(0, 10));
                              }
                            }}
                            className="flex items-center gap-2 flex-1 cursor-pointer"
                          >
                            <User className="h-3 w-3" />
                            {t.name || 'Unbekannt'}
                            {t.finanzierungsart && (
                              <span className="text-xs bg-blue-100 px-1 rounded">{t.finanzierungsart}</span>
                            )}
                            {selectedEinzelTeilnehmer?.id === t.id && (
                              <span className="text-xs bg-green-600 text-white px-2 py-0.5 rounded">Ausgewählt</span>
                            )}
                          </div>
                          {/* DEAKTIVIERT: Lösch-Button aus Sicherheitsgründen entfernt */}
                        </div>
                      ))}
                    </div>
                  </div>
                  
                  <div>
                    <label className="text-sm font-medium mb-2 block">Kurstyp auswählen *</label>
                    <Select 
                      value={selectedKursTyp} 
                      onValueChange={(val) => {
                        setSelectedKursTyp(val);
                        // Automatisch passende Vorlage setzen
                        if (val === 'gerontopsychiatrie') {
                          setSelectedVorlageId('geronto');
                        } else if (val === 'pflegedienstleitung') {
                          setSelectedVorlageId('pdl');
                        } else if (val === 'ambulanter_pflegeassistent') {
                          setSelectedVorlageId('ambulant');
                        } else if (val === 'palliativ' || val === 'palliative_care') {
                          setSelectedVorlageId('palliativ');
                        } else if (val.includes('lg1') || val.includes('lg2') || val.includes('behandlungspflege')) {
                          setSelectedVorlageId('lg');
                        } else if (val === 'wundexperte') {
                          setSelectedVorlageId('wundexperte');
                        } else if (val === 'zusatzqualifikation_wunden') {
                          setSelectedVorlageId('chronische_wunden');
                        } else {
                          setSelectedVorlageId('pfh');
                        }
                      }}
                    >
                      <SelectTrigger className="w-full">
                        <SelectValue placeholder="Kurstyp wählen..." />
                      </SelectTrigger>
                      <SelectContent>
                        {KURSE.map((k) => (
                          <SelectItem key={k.id} value={k.id}>
                            {k.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-gray-500 mt-1">
                      Wählen Sie den Kurs, unter dem das Klassenbuch angezeigt werden soll.
                    </p>
                  </div>
                  
                  <div>
                    <label className="text-sm font-medium mb-2 block">Stundenplan-Vorlage *</label>
                    <Select 
                      value={selectedVorlageId} 
                      onValueChange={setSelectedVorlageId}
                    >
                      <SelectTrigger className="w-full">
                        <SelectValue placeholder="Vorlage wählen..." />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="pfh">Pflegehilfskraft (66 Tage / 528 UE)</SelectItem>
                        <SelectItem value="lg">LG1 & LG2 Behandlungspflege (32 Tage / 208 UE)</SelectItem>
                        <SelectItem value="ambulant">Ambulanter Pflegeassistent (46 Tage / 300 UE)</SelectItem>
                        <SelectItem value="palliativ">Palliativ Care (40 Tage / 160 UE)</SelectItem>
                        <SelectItem value="wundexperte">Wundexperte (21 Tage / 168 UE)</SelectItem>
                        <SelectItem value="chronische_wunden">Chronisch schwerheilende Wunden (11 Tage / 84 UE)</SelectItem>
                        <SelectItem value="geronto">Gerontopsychiatrie (60 Tage / 480 UE)</SelectItem>
                        <SelectItem value="pdl">Pflegedienstleitung PDL (120 Tage / 720 UE)</SelectItem>
                        <SelectItem value="intensivpflege">Basiskurs Intensivpflege §132l (15 Tage / 120 UE)</SelectItem>
                        <SelectItem value="praxisanleitung">Praxisanleitung (38 Tage / 300 UE)</SelectItem>
                        <SelectItem value="pflegeberater">Pflegeberater §7a SGB XI (60 Tage / 480 UE)</SelectItem>
                        <SelectItem value="qualitaetsbeauftragter">Qualitätsbeauftragter (20 Tage / 160 UE)</SelectItem>
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-gray-500 mt-1">
                      Der Stundenplan bestimmt die Kursinhalte pro Tag.
                    </p>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm font-medium">Kurs-Startdatum *</label>
                      <Input 
                        type="date"
                        value={historischStartDatum}
                        onChange={(e) => setHistorischStartDatum(e.target.value)}
                        className="mt-1"
                        data-testid="historisch-start-datum"
                      />
                    </div>
                    <div>
                      <label className="text-sm font-medium">Kurs-Enddatum *</label>
                      <Input 
                        type="date"
                        value={historischEndDatum}
                        onChange={(e) => setHistorischEndDatum(e.target.value)}
                        className="mt-1"
                        data-testid="historisch-end-datum"
                      />
                    </div>
                  </div>
                  <p className="text-xs text-gray-500">
                    Die Unterrichtstage werden gleichmäßig über den Zeitraum verteilt (nur Werktage).
                  </p>

                  {/* NEU: Modus-Auswahl - Komplettes Klassenbuch vs. nur bis heute */}
                  <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg" data-testid="klassenbuch-modus">
                    <p className="text-sm font-medium text-amber-900 mb-2">Umfang der Vorschau:</p>
                    <div className="space-y-2">
                      <label className="flex items-start gap-2 cursor-pointer">
                        <input type="radio"
                          checked={!nurBisHeute}
                          onChange={() => setNurBisHeute(false)}
                          className="mt-0.5"
                          data-testid="modus-komplett"
                        />
                        <div className="text-sm">
                          <span className="font-semibold text-amber-900">Komplettes Soll-Klassenbuch</span>
                          <div className="text-xs text-amber-800">
                            Liefert immer die volle Anzahl an Unterrichtstagen
                            (LG1/LG2: 32, Pflegehilfskraft: 66 etc.). Empfohlen für historische / abgeschlossene Kurse.
                          </div>
                        </div>
                      </label>
                      <label className="flex items-start gap-2 cursor-pointer">
                        <input type="radio"
                          checked={nurBisHeute}
                          onChange={() => setNurBisHeute(true)}
                          className="mt-0.5"
                          data-testid="modus-zwischenstand"
                        />
                        <div className="text-sm">
                          <span className="font-semibold text-amber-900">Nur Zwischenstand (bis heute)</span>
                          <div className="text-xs text-amber-800">
                            Nur die Tage bis einschließlich heute — für laufende Kurse, bei denen
                            zukünftige Tage noch nicht im Klassenbuch erscheinen sollen.
                          </div>
                        </div>
                      </label>
                    </div>
                  </div>
                  
                  <div>
                    <label className="text-sm font-medium">Standard-Dozent</label>
                    <select 
                      value={historischDozent}
                      onChange={(e) => setHistorischDozent(e.target.value)}
                      className="w-full border rounded-md px-3 py-2 text-sm mt-1"
                    >
                      <option value="Dickie (MD)">Dickie (MD)</option>
                      <option value="Müller (JM)">Müller (JM)</option>
                      <option value="Lorke (AL)">Lorke (AL)</option>
                    </select>
                  </div>
                  
                  <div className="p-3 bg-green-50 border border-green-200 rounded-lg text-sm">
                    <p className="font-medium text-green-700">✓ Was wird erstellt:</p>
                    <ul className="text-green-600 mt-1 space-y-1">
                      <li>• {{'pdl': '120', 'geronto': '60', 'lg': '32', 'wundexperte': '21', 'chronische_wunden': '11', 'intensivpflege': '15', 'praxisanleitung': '38', 'pflegeberater': '60', 'qualitaetsbeauftragter': '20', 'ambulant': '46', 'palliativ': '40'}[selectedVorlageId] || '66'} Unterrichtstage ({{'pdl': 'Pflegedienstleitung', 'geronto': 'Gerontopsychiatrie', 'lg': 'LG1 & LG2 Behandlungspflege', 'wundexperte': 'Wundexperte', 'chronische_wunden': 'Chronisch schwerheilende Wunden', 'intensivpflege': 'Intensivpflege §132l', 'praxisanleitung': 'Praxisanleitung', 'pflegeberater': 'Pflegeberater §7a', 'qualitaetsbeauftragter': 'Qualitätsbeauftragter', 'ambulant': 'Ambulanter Pflegeassistent', 'palliativ': 'Palliativ Care'}[selectedVorlageId] || 'Pflegehilfskraft'} Curriculum)</li>
                      <li>• Anwesenheitsliste für {selectedEinzelTeilnehmer ? <span className="font-bold">{selectedEinzelTeilnehmer.name}</span> : `alle ${selectedDurchgang.teilnehmer_count} Teilnehmer`}</li>
                      <li>• Unterricht verteilt auf Werktage zwischen {historischStartDatum || '??'} und {historischEndDatum || '??'}</li>
                      <li>• Realistische Anwesenheit (2-3 entschuldigte Fehltage pro TN)</li>
                    </ul>
                  </div>
                  
                  {/* PDF Optionen */}
                  <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg space-y-3">
                    <p className="font-medium text-blue-700">PDF-Optionen:</p>
                    
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input 
                        type="checkbox" 
                        checked={downloadPdfsAfterCreate}
                        onChange={(e) => setDownloadPdfsAfterCreate(e.target.checked)}
                        className="w-4 h-4 rounded border-gray-300"
                      />
                      <span className="text-sm text-blue-700">
                        PDFs direkt herunterladen (für jeden Teilnehmer)
                      </span>
                    </label>
                    
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input 
                        type="checkbox" 
                        checked={savePdfsToTeilnehmer}
                        onChange={(e) => setSavePdfsToTeilnehmer(e.target.checked)}
                        className="w-4 h-4 rounded border-gray-300"
                      />
                      <span className="text-sm text-blue-700">
                        PDFs beim Teilnehmer speichern (in Anhänge)
                      </span>
                    </label>
                  </div>
                </>
              )}
            </div>
            
            <div className="flex justify-end gap-3 pt-2 border-t">
              <Button variant="outline" onClick={() => {
                setShowHistorischDialog(false);
                setSelectedDurchgang(null);
                setSelectedEinzelTeilnehmer(null);
                setHistorischStartDatum('');
                setHistorischEndDatum('');
                setSearchTeilnehmer('');
              }}>
                Abbrechen
              </Button>
              <Button 
                className="bg-amber-600 hover:bg-amber-700 text-white"
                onClick={handleCreateHistorisch}
                disabled={creatingHistorisch || loadingVorschau || !selectedDurchgang}
                data-testid="create-historisch-btn"
              >
                {loadingVorschau ? 'Lade Vorschau...' : creatingHistorisch ? 'Erstelle...' : 'Vorschau & Prüfen'}
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* NEU: Vorschau-Dialog für Anwesenheitsprüfung */}
        <Dialog open={showVorschauDialog} onOpenChange={setShowVorschauDialog}>
          <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <FileText className="h-5 w-5 text-blue-600" />
                Anwesenheit prüfen und anpassen
              </DialogTitle>
            </DialogHeader>
            
            {vorschauData && (
              <div className="space-y-6">
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <p className="text-sm text-blue-800">
                    <strong>Zeitraum:</strong> {vorschauData.start_datum} bis {vorschauData.end_datum} ({vorschauData.total_tage} Unterrichtstage)
                  </p>
                  <p className="text-sm text-blue-600 mt-1">
                    Klicken Sie auf "A" (Anwesend) oder "E" (Entschuldigt), um den Status zu ändern. 
                    <strong> Datum, Themen und Zeilen können direkt bearbeitet werden.</strong>
                  </p>
                </div>

                {vorschauData.teilnehmer?.map((tn, tnIndex) => (
                  <div key={tn.teilnehmer_id} className="border rounded-lg overflow-hidden">
                    <div className="bg-gray-100 px-4 py-3 flex justify-between items-center">
                      <div>
                        <h4 className="font-semibold">{tn.teilnehmer_name}</h4>
                        <p className="text-sm text-gray-500">
                          Anwesend: {tn.statistik?.anwesend} | Entschuldigt: {tn.statistik?.entschuldigt} | 
                          Anwesenheit: {tn.statistik?.prozent}% | Tage: {tn.tage?.length}
                        </p>
                      </div>
                    </div>
                    
                    <div className="p-4 overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="bg-gray-50">
                            <th className="px-2 py-1 text-left w-12">Tag</th>
                            <th className="px-2 py-1 text-left w-32">Datum</th>
                            <th className="px-2 py-1 text-left">Thema</th>
                            <th className="px-2 py-1 text-center w-24">Status</th>
                            <th className="px-2 py-1 text-center w-20">Aktion</th>
                          </tr>
                        </thead>
                        <tbody>
                          {tn.tage?.map((tag, tagIndex) => (
                            <tr key={`${tag.datum}-${tagIndex}`} className={tag.status === 'entschuldigt' ? 'bg-orange-50' : ''}>
                              <td className="px-2 py-1 text-gray-500">{tag.tag}</td>
                              <td className="px-2 py-1">
                                <input
                                  type="date"
                                  value={tag.datum || ''}
                                  onChange={(e) => handleChangeDatum(tnIndex, tagIndex, e.target.value)}
                                  className="w-full px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:border-blue-500"
                                />
                              </td>
                              <td className="px-2 py-1">
                                <input
                                  type="text"
                                  value={tag.thema || ''}
                                  onChange={(e) => handleChangeThema(tnIndex, tagIndex, e.target.value)}
                                  className="w-full px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:border-blue-500"
                                  placeholder="Thema eingeben..."
                                />
                              </td>
                              <td className="px-2 py-1 text-center">
                                <div className="flex justify-center gap-1">
                                  <button
                                    onClick={() => handleChangeAnwesenheit(tnIndex, tagIndex, 'anwesend')}
                                    className={`px-2 py-1 rounded text-xs font-medium transition-colors ${
                                      tag.status === 'anwesend' 
                                        ? 'bg-green-500 text-white' 
                                        : 'bg-gray-200 text-gray-600 hover:bg-green-100'
                                    }`}
                                  >
                                    A
                                  </button>
                                  <button
                                    onClick={() => handleChangeAnwesenheit(tnIndex, tagIndex, 'entschuldigt')}
                                    className={`px-2 py-1 rounded text-xs font-medium transition-colors ${
                                      tag.status === 'entschuldigt' 
                                        ? 'bg-orange-500 text-white' 
                                        : 'bg-gray-200 text-gray-600 hover:bg-orange-100'
                                    }`}
                                  >
                                    E
                                  </button>
                                </div>
                              </td>
                              <td className="px-2 py-1 text-center">
                                <div className="flex justify-center gap-1">
                                  <button
                                    onClick={() => handleAddTag(tnIndex, tagIndex)}
                                    className="px-1.5 py-1 rounded text-xs bg-blue-100 text-blue-600 hover:bg-blue-200"
                                    title="Zeile nach diesem Tag einfügen"
                                  >
                                    +
                                  </button>
                                  <button
                                    onClick={() => handleDeleteTag(tnIndex, tagIndex)}
                                    className="px-1.5 py-1 rounded text-xs bg-red-100 text-red-600 hover:bg-red-200"
                                    title="Diese Zeile löschen"
                                  >
                                    ×
                                  </button>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                ))}

                <div className="flex justify-between items-center pt-4 border-t">
                  <Button variant="outline" onClick={() => setShowVorschauDialog(false)}>
                    Abbrechen
                  </Button>
                  <Button 
                    onClick={() => handleSaveWithAnwesenheit(false)}
                    disabled={creatingHistorisch}
                    className="bg-green-600 hover:bg-green-700 text-white"
                  >
                    {creatingHistorisch ? 'Speichere...' : 'Klassenbuch speichern & PDFs erstellen'}
                  </Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* NEU: Einfacher Modal für einzelnen Teilnehmer Klassenbuch bearbeiten */}
        {showTeilnehmerDialog && (
          <div className="fixed inset-0 z-[9999] flex items-center justify-center">
            {/* Overlay */}
            <div 
              className="fixed inset-0 bg-black bg-opacity-50" 
              onClick={() => setShowTeilnehmerDialog(false)}
            />
            {/* Modal Content */}
            <div className="relative bg-white rounded-lg shadow-xl max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto z-[10000]">
              {/* Header */}
              <div className="sticky top-0 bg-white border-b px-6 py-4 flex justify-between items-center">
                <h2 className="text-lg font-bold flex items-center gap-2">
                  <User className="h-5 w-5 text-blue-600" />
                  <span>Klassenbuch: {selectedTeilnehmerForEdit?.vorname || ''} {selectedTeilnehmerForEdit?.nachname || ''}</span>
                  {selectedTeilnehmerForEdit && istBildungsgutschein(selectedTeilnehmerForEdit) && (
                    <span className="px-2 py-0.5 text-xs font-bold bg-blue-600 text-white rounded">BG</span>
                  )}
                </h2>
                <button 
                  onClick={() => setShowTeilnehmerDialog(false)}
                  className="text-gray-500 hover:text-gray-700 text-2xl"
                >
                  ×
                </button>
              </div>
              
              {/* Body */}
              <div className="p-6 min-h-[200px]">
                {loadingTeilnehmerKlassenbuch ? (
                  <div className="text-center py-8 text-gray-500">
                    <div className="animate-spin h-8 w-8 border-4 border-blue-500 border-t-transparent rounded-full mx-auto mb-4"></div>
                    Lade Klassenbuch-Daten...
                  </div>
                ) : teilnehmerAnwesenheitEdit.length === 0 ? (
                  <div className="text-center py-8">
                    <p className="text-gray-500 mb-4">Noch keine Klassenbuch-Einträge für diesen Teilnehmer.</p>
                    <p className="text-sm text-gray-400 mb-6">
                      Verwenden Sie "Historisches Klassenbuch erstellen" um rückwirkend Einträge anzulegen.
                    </p>
                    <div className="flex justify-center gap-2">
                      <Button variant="outline" onClick={() => setShowTeilnehmerDialog(false)}>
                        Schließen
                      </Button>
                      <Button 
                        onClick={() => {
                          setShowTeilnehmerDialog(false);
                          setShowHistorischDialog(true);
                        }}
                        className="bg-blue-600 hover:bg-blue-700 text-white"
                      >
                        Historisches Klassenbuch erstellen
                      </Button>
                    </div>
                  </div>
                ) : (
                <div className="space-y-4">
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm flex justify-between items-center">
                    <p className="text-blue-700">
                      <strong>{teilnehmerAnwesenheitEdit.length} Unterrichtstage</strong> | 
                      Anwesend: {teilnehmerAnwesenheitEdit.filter(t => t.status === 'anwesend').length} |
                      Entschuldigt: {teilnehmerAnwesenheitEdit.filter(t => t.status === 'entschuldigt').length} |
                      Fehlend: {teilnehmerAnwesenheitEdit.filter(t => t.status === 'fehlend').length}
                    </p>
                    <Button 
                      size="sm" 
                      variant="outline"
                      onClick={handleAddTeilnehmerTag}
                      className="text-blue-600 border-blue-300"
                    >
                      <Plus className="h-3 w-3 mr-1" />
                      Tag hinzufügen
                    </Button>
                  </div>
                  
                  <div className="border rounded-lg overflow-hidden max-h-[50vh] overflow-y-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-gray-100 sticky top-0">
                        <tr>
                          <th className="px-2 py-2 text-left w-10">Tag</th>
                          <th className="px-2 py-2 text-left w-32">Datum</th>
                          <th className="px-2 py-2 text-left">Thema</th>
                          <th className="px-2 py-2 text-center w-20">UE</th>
                          <th className="px-2 py-2 text-center w-28">Status</th>
                          <th className="px-2 py-2 text-center w-10"></th>
                        </tr>
                      </thead>
                      <tbody>
                        {teilnehmerAnwesenheitEdit.map((tag, idx) => (
                          <tr 
                            key={idx} 
                            className={`border-b ${
                              tag.status === 'entschuldigt' ? 'bg-yellow-50' : 
                              tag.status === 'fehlend' ? 'bg-red-50' : ''
                            }`}
                          >
                            <td className="px-2 py-1 text-gray-500 text-center">{tag.tag_im_kurs || idx + 1}</td>
                            <td className="px-2 py-1">
                              <input
                                type="date"
                                value={tag.datum || ''}
                                onChange={(e) => handleChangeTeilnehmerEintrag(idx, 'datum', e.target.value)}
                                className="w-full px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:border-blue-500"
                              />
                            </td>
                            <td className="px-2 py-1">
                              <input
                                type="text"
                                value={tag.thema || ''}
                                onChange={(e) => handleChangeTeilnehmerEintrag(idx, 'thema', e.target.value)}
                                placeholder="Thema eingeben..."
                                className="w-full px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:border-blue-500"
                              />
                            </td>
                            <td className="px-2 py-1">
                              <input
                                type="number"
                                value={tag.ue || 8}
                                onChange={(e) => handleChangeTeilnehmerEintrag(idx, 'ue', parseInt(e.target.value) || 8)}
                                min="1"
                                max="12"
                                className="w-full px-2 py-1 border border-gray-300 rounded text-sm text-center focus:outline-none focus:border-blue-500"
                              />
                            </td>
                            <td className="px-2 py-1">
                              <div className="flex justify-center gap-1">
                                <button
                                  onClick={() => handleChangeTeilnehmerAnwesenheit(idx, 'anwesend')}
                                  className={`px-2 py-1 rounded text-xs font-medium ${
                                    tag.status === 'anwesend' 
                                      ? 'bg-green-500 text-white' 
                                      : 'bg-gray-200 hover:bg-green-100'
                                  }`}
                                  title="Anwesend"
                                >
                                  ✓
                                </button>
                                <button
                                  onClick={() => handleChangeTeilnehmerAnwesenheit(idx, 'entschuldigt')}
                                  className={`px-2 py-1 rounded text-xs font-medium ${
                                    tag.status === 'entschuldigt' 
                                      ? 'bg-yellow-500 text-white' 
                                      : 'bg-gray-200 hover:bg-yellow-100'
                                  }`}
                                  title="Entschuldigt"
                                >
                                  E
                                </button>
                                <button
                                  onClick={() => handleChangeTeilnehmerAnwesenheit(idx, 'fehlend')}
                                  className={`px-2 py-1 rounded text-xs font-medium ${
                                    tag.status === 'fehlend' 
                                      ? 'bg-red-500 text-white' 
                                      : 'bg-gray-200 hover:bg-red-100'
                                  }`}
                                  title="Fehlend"
                                >
                                  ✗
                                </button>
                              </div>
                            </td>
                            <td className="px-2 py-1">
                              <button
                                onClick={() => handleRemoveTeilnehmerTag(idx)}
                                className="text-red-400 hover:text-red-600 p-1"
                                title="Eintrag entfernen"
                              >
                                <Trash2 className="h-4 w-4" />
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  
                  {/* Footer Buttons */}
                  <div className="flex justify-between items-center pt-4 border-t">
                    <Button variant="outline" onClick={() => setShowTeilnehmerDialog(false)}>
                      Schließen
                    </Button>
                    <div className="flex gap-2">
                      <Button 
                        variant="outline"
                        onClick={handleSaveTeilnehmerAnwesenheit}
                      >
                        Änderungen speichern
                      </Button>
                      <Button 
                        onClick={handleDownloadTeilnehmerPdf}
                        className="bg-blue-600 hover:bg-blue-700 text-white"
                      >
                        <FileText className="h-4 w-4 mr-2" />
                        PDF herunterladen
                      </Button>
                    </div>
                  </div>
                </div>
                )}
              </div>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {KURSE.map((kurs) => (
            <Card 
              key={kurs.id}
              className="cursor-pointer hover:shadow-lg transition-all border-2 hover:border-[#D9775A]"
              onClick={() => handleKursClick(kurs)}
            >
              <CardContent className="p-4">
                <h3 className="font-semibold text-lg mb-1">{kurs.kurzname}</h3>
                <p className="text-sm text-gray-500 mb-2">{kurs.name}</p>
                <div className="flex items-center gap-2 text-sm">
                  <Users className="h-4 w-4 text-gray-400" />
                  <span className="font-medium">{kursTeilnehmerCount[kurs.id] || 0} Teilnehmer</span>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  // ANSICHT 2: Klassenbuch-Blatt
  return (
    <div className="p-6 max-w-5xl mx-auto">
      {/* Header */}
      <div className="mb-4 flex justify-between items-center">
        <Button variant="ghost" onClick={handleBack} className="mb-2">
          <ArrowLeft className="h-4 w-4 mr-2" />
          Zurück
        </Button>
        
        {/* Import-Button */}
        <Button 
          variant="outline" 
          className="bg-violet-50 border-violet-300 text-violet-700 hover:bg-violet-100"
          onClick={() => {
            loadVorlagen();
            setShowImportDialog(true);
          }}
        >
          <Upload className="h-4 w-4 mr-2" />
          Stundenplan aus Vorlage importieren
        </Button>
      </div>

      {/* Import-Dialog */}
      <Dialog open={showImportDialog} onOpenChange={setShowImportDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5 text-violet-600" />
              Klassenbuch aus Stundenplan-Vorlage erstellen
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm">
              <p className="font-medium text-amber-700">Hinweis:</p>
              <p className="text-amber-600">
                Dies erstellt Klassenbuch-Einträge für ALLE Kurstage basierend auf der Vorlage.
                Nur für Kurse OHNE existierende Einträge möglich!
              </p>
            </div>
            
            <div>
              <label className="text-sm font-medium">Kurs</label>
              <Input value={selectedKurs?.name || ''} disabled className="bg-gray-100" />
            </div>
            
            <div>
              <label className="text-sm font-medium">Kursstart (erster Unterrichtstag) *</label>
              <Input 
                type="date" 
                value={importStartDatum}
                onChange={(e) => setImportStartDatum(e.target.value)}
              />
            </div>
            
            <div>
              <label className="text-sm font-medium">Standard-Dozent (Nachname + Kürzel)</label>
              <select 
                value={importDozent}
                onChange={(e) => setImportDozent(e.target.value)}
                className="w-full border rounded-md px-3 py-2 text-sm mt-1"
              >
                <option value="Dickie (MD)">Dickie (MD)</option>
                <option value="Müller (JM)">Müller (JM)</option>
                <option value="Lorke (AL)">Lorke (AL)</option>
              </select>
            </div>
            
            {vorlagen && (
              <div className="p-3 bg-gray-50 rounded-lg text-sm">
                <p className="font-medium">Verfügbare Vorlage:</p>
                <p>{vorlagen.vorlagen?.[0]?.name} ({vorlagen.vorlagen?.[0]?.tage} Unterrichtstage)</p>
              </div>
            )}
          </div>
          
          <div className="flex justify-end gap-3">
            <Button variant="outline" onClick={() => setShowImportDialog(false)}>
              Abbrechen
            </Button>
            <Button 
              className="bg-violet-600 hover:bg-violet-700 text-white"
              onClick={handleImport}
              disabled={importing || !importStartDatum}
            >
              {importing ? 'Importiere...' : 'Klassenbuch erstellen'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Klassenbuch-Blatt */}
      <div className="bg-white border-2 border-gray-300 rounded-lg shadow-lg">
        {/* Kopfzeile */}
        <div className="bg-gray-100 p-4 border-b-2 border-gray-300">
          <div className="flex justify-between items-start">
            <div>
              {BG_KURSE.includes(selectedKurs.id) ? (
                <Select value={selectedKurs.id} onValueChange={kursWechseln}>
                  <SelectTrigger className="w-[340px] text-lg font-bold bg-white" data-testid="kurs-switch">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {BG_KURSE.map(id => (
                      <SelectItem key={id} value={id} data-testid={`kurs-switch-${id}`}>{KURSE.find(k => k.id === id)?.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : (
                <h1 className="text-xl font-bold">{selectedKurs.name}</h1>
              )}
              <p className="text-gray-600">Klassenbuch</p>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={async () => {
                  toast.info("Stundenplan-PDF (AZAV) wird erstellt...");
                  try {
                    const res = await axios.get(`${API}/klassenbuch-neu/kurs/${selectedKurs.id}/stundenplan-azav-pdf`, { responseType: 'blob' });
                    const url = window.URL.createObjectURL(new Blob([res.data]));
                    const a = document.createElement('a'); a.href = url;
                    a.setAttribute('download', `Stundenplan_${selectedKurs.name.replace(/\s+/g, '_')}.pdf`);
                    document.body.appendChild(a); a.click(); a.remove();
                    window.URL.revokeObjectURL(url);
                    toast.success("Stundenplan-PDF heruntergeladen");
                  } catch (err) {
                    let detail = "Fehler bei Stundenplan-PDF";
                    if (err.response?.data instanceof Blob) {
                      try { const t = await err.response.data.text(); const j = JSON.parse(t); detail = j.detail || detail; } catch (e) {}
                    } else if (err.response?.data?.detail) detail = err.response.data.detail;
                    toast.error(detail);
                  }
                }}
                className="px-3 py-2 rounded bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium flex items-center gap-2"
                data-testid="btn-stundenplan-azav-pdf"
                title="AZAV-konformer Stundenplan — reiner Maßnahmen-Plan, ohne Anwesenheit, mit voller Dozentennamen (kein QM-Formblatt; operatives Dokument)"
              >
                <FileText className="h-4 w-4" /> Stundenplan PDF
              </button>
              <Calendar className="h-5 w-5 text-gray-500 ml-2" />
              <Input
                type="date"
                value={datum}
                onChange={(e) => handleDatumChange(e.target.value)}
                className="w-40"
              />
            </div>
          </div>
        </div>

        <OffeneTageHinweis kursId={selectedKurs.id} kohorte={kohorteFuer(teilnehmer, selectedKurs.id, datum)} reloadKey={reloadKey} onSelectDay={handleDatumChange} />
        <KlassenbuchWoche kursId={selectedKurs.id} datum={datum} kohorte={kohorteFuer(teilnehmer, selectedKurs.id, datum)} reloadKey={reloadKey} onSelectDay={handleDatumChange} />

        {/* UE-Einträge */}
        <div className="p-4 border-b-2 border-gray-300 bg-amber-50">
          {/* Thema aus Vorlage auswählen */}
          <div className="mb-4 p-3 bg-violet-50 border border-violet-200 rounded-lg">
            <div className="flex items-center gap-3">
              <FileText className="h-5 w-5 text-violet-600 flex-shrink-0" />
              <div className="flex-1">
                <label className="text-sm font-medium text-violet-700">Thema aus Stundenplan-Vorlage wählen:</label>
                <Select onValueChange={handleThemaSelect} value={selectedThema?.tag?.toString() || ""}>
                  <SelectTrigger className="w-full mt-1 bg-white">
                    <SelectValue placeholder="Thema auswählen (optional)..." />
                  </SelectTrigger>
                  <SelectContent className="max-h-[300px]">
                    {themenVorlage.map((t) => (
                      <SelectItem key={t.tag} value={t.tag.toString()}>
                        <span className="font-medium">Tag {t.tag}:</span> {t.thema}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            {selectedThema && (
              <div className="mt-2 p-2 bg-white rounded text-sm">
                <span className="font-medium">Lernziel:</span> {selectedThema.lernziel}
              </div>
            )}
          </div>
          
          <div className="flex justify-between items-center mb-3">
            <h3 className="font-semibold flex items-center gap-2">
              <BookOpen className="h-4 w-4" />
              Unterrichtseinheiten am {new Date(datum).toLocaleDateString('de-DE')}
            </h3>
            <Button variant="outline" size="sm" onClick={addUeEintrag}>
              <Plus className="h-4 w-4 mr-1" />
              UE hinzufügen
            </Button>
          </div>

          <div className="space-y-4">
            {/* UE-Zeilen */}
            {ueEintraege.map((eintrag, index) => (
              <div key={index} className="bg-gray-50 rounded-lg p-3 space-y-2">
                {/* Erste Zeile: UE-Nr, Thema, Dozent, Löschen */}
                <div className="grid grid-cols-[60px,1fr,180px,40px] gap-2 items-center">
                  <Input
                    type="number"
                    min="1"
                    value={eintrag.ue}
                    onChange={(e) => updateUeEintrag(index, 'ue', parseInt(e.target.value) || 1)}
                    className="text-center font-medium bg-white"
                  />
                  <Input
                    value={eintrag.thema}
                    onChange={(e) => updateUeEintrag(index, 'thema', e.target.value)}
                    placeholder="Thema / Inhalt"
                    className="bg-white"
                  />
                  <select
                    value={eintrag.dozent}
                    onChange={(e) => updateUeEintrag(index, 'dozent', e.target.value)}
                    className="border rounded-md px-2 py-1 text-sm bg-white"
                  >
                    <option value="Dickie (MD)">Dickie (MD)</option>
                    <option value="Müller (JM)">Müller (JM)</option>
                    <option value="Lorke (AL)">Lorke (AL)</option>
                  </select>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => removeUeEintrag(index)}
                    disabled={ueEintraege.length <= 1}
                    className="text-gray-400 hover:text-red-500"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
                {/* Zweite Zeile: Notizen (was genau besprochen wurde) */}
                <div className="pl-[68px] pr-[48px]">
                  <textarea
                    value={eintrag.notizen || ''}
                    onChange={(e) => updateUeEintrag(index, 'notizen', e.target.value)}
                    placeholder="Was wurde besprochen? (Details, Schwerpunkte, Übungen...)"
                    className="w-full px-3 py-2 text-sm border border-gray-200 rounded-md bg-white resize-none focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    rows={2}
                  />
                </div>
              </div>
            ))}
          </div>
          
          <p className="text-sm text-gray-500 mt-2">
            Gesamt: <strong>{uePlan?.ue_plan ?? ueEintraege.length} UE</strong> {uePlan?.ue_plan ? 'laut Stundenplan' : 'eingetragen'}
            {ueEintraege.some(e => e.vorbefuellt) && (
              <span data-testid="thema-vorbefuellt" className="ml-2 px-2 py-0.5 text-xs font-semibold rounded-full bg-[#FEF4EE] text-[#9A3412] border border-[#F8D6C3]">
                Thema aus Stundenplan vorbefüllt – nur bei Änderung anpassen
              </span>
            )}
          </p>
        </div>

        {/* Anwesenheitsliste */}
        <div className="p-4">
          <div className="flex flex-col gap-3 mb-3">
            <div className="flex justify-between items-center">
              <h3 className="font-semibold flex items-center gap-2">
                <Users className="h-4 w-4" />
                Anwesenheit ({teilnehmer.length} Teilnehmer)
              </h3>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={alleAnwesend} data-testid="btn-alle-anwesend">
                  Alle anwesend
                </Button>
                <Button variant="outline" size="sm" onClick={alleFehlend} className="border-red-300 text-red-700 hover:bg-red-50" data-testid="btn-alle-fehlend">
                  Alle fehlend
                </Button>
              </div>
            </div>
            <TagStatusHinweis tagDoc={tagDoc} />
            
            {/* BG-Filter - nur bei BG-fähigen Kursen anzeigen */}
            {BG_FAEHIGE_KURSE.includes(selectedKurs?.id) && (
              <div className="flex items-center gap-4 p-2 bg-blue-50 rounded-lg border border-blue-200">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={showOnlyBG}
                    onChange={(e) => setShowOnlyBG(e.target.checked)}
                    className="w-4 h-4 rounded border-blue-300 text-blue-600 focus:ring-blue-500"
                  />
                  <span className="text-sm font-medium text-blue-800">
                    Nur Bildungsgutschein (AZAV)
                  </span>
                </label>
                <span className="text-xs text-blue-600">
                  {teilnehmer.filter(t => istBildungsgutschein(t)).length} von {teilnehmer.length} mit BG
                </span>
              </div>
            )}
          </div>

          {loading ? (
            <div className="text-center py-8 text-gray-500">Laden...</div>
          ) : teilnehmer.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              Keine Teilnehmer in diesem Kurs
            </div>
          ) : (
            <div className="border rounded-lg overflow-hidden">
              {/* Tabellen-Header */}
              <div className="grid grid-cols-[1fr,auto,auto] bg-gray-100 border-b font-medium text-sm">
                <div className="p-3">Name</div>
                <div className="p-3 flex gap-2 text-center">
                  <span className="w-10 text-green-600 text-xs" title="Anwesend">anw.</span>
                  <span className="w-10 text-orange-600 text-xs" title="Teilweise anwesend">teilw.</span>
                  <span className="w-10 text-yellow-600 text-xs" title="Abwesend entschuldigt">entsch.</span>
                  <span className="w-10 text-red-600 text-xs" title="Abwesend unentschuldigt">unent.</span>
                </div>
                <div className="p-3 w-12 text-center" title="Stundenplan PDF"></div>
              </div>
              
              {/* Teilnehmer-Liste - gefiltert und sortiert (BG oben) */}
              {teilnehmer
                .filter(t => !showOnlyBG || istBildungsgutschein(t))
                .sort((a, b) => {
                  // BG-Teilnehmer immer oben
                  const aIsBG = istBildungsgutschein(a);
                  const bIsBG = istBildungsgutschein(b);
                  if (aIsBG && !bIsBG) return -1;
                  if (!aIsBG && bIsBG) return 1;
                  // Sonst alphabetisch nach Nachname
                  return (a.nachname || '').localeCompare(b.nachname || '');
                })
                .map((t, index) => {
                const hasBG = istBildungsgutschein(t);
                return (
                <div 
                  key={t.id}
                  className={`grid grid-cols-[1fr,auto,auto] border-b last:border-b-0 ${
                    hasBG ? 'bg-blue-50' : index % 2 === 0 ? 'bg-white' : 'bg-gray-50'
                  }`}
                >
                  <div className="p-3 flex items-center gap-2">
                    {/* Name (nicht klickbar, nur Text) */}
                    <span className="font-medium">
                      {t.vorname} {t.nachname}
                    </span>
                    {hasBG && (
                      <span className="px-2 py-0.5 text-xs font-bold bg-blue-600 text-white rounded" title="Bildungsgutschein - AZAV relevant">
                        BG
                      </span>
                    )}
                    <EinwilligungBadge ew={einwilligungen[t.id]} />
                    {/* Klassenbuch-Button - öffnet Dialog */}
                    <button
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        handleOpenTeilnehmerKlassenbuch(t);
                      }}
                      className="ml-auto px-2 py-1 text-xs bg-blue-100 text-blue-700 hover:bg-blue-200 rounded flex items-center gap-1"
                      title="Klassenbuch für diesen Teilnehmer bearbeiten"
                      data-testid={`klassenbuch-btn-${t.id}`}
                    >
                      <BookOpen className="h-3 w-3" />
                      Klassenbuch
                    </button>
                    {/* Link zur Akte */}
                    <a 
                      href={`/teilnehmer?id=${t.id}`}
                      className="text-gray-400 hover:text-gray-600 p-1"
                      title="Zur Teilnehmer-Akte"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <ExternalLink className="h-3 w-3" />
                    </a>
                  </div>
                  <div className="p-2 flex gap-2">
                    {/* Anwesend */}
                    <button
                      data-testid={`status-anwesend-${t.id}`}
                      title="Anwesend"
                      onClick={() => setStatus(t.id, 'anwesend')}
                      className={`w-10 h-9 rounded border-2 flex items-center justify-center transition-all ${
                        anwesenheit[t.id] === 'anwesend' 
                          ? 'bg-green-500 border-green-600 text-white' 
                          : 'border-gray-300 hover:border-green-400 text-gray-400 hover:text-green-500'
                      }`}
                    >
                      <Check className="h-4 w-4" />
                    </button>
                    
                    {/* Teilweise */}
                    <button
                      onClick={() => setStatus(t.id, 'teilweise')}
                      data-testid={`status-teilweise-${t.id}`}
                      title="Teilweise anwesend"
                      className={`w-10 h-9 rounded border-2 flex items-center justify-center transition-all text-xs font-bold ${
                        anwesenheit[t.id] === 'teilweise'
                          ? 'bg-orange-500 border-orange-600 text-white'
                          : 'border-gray-300 hover:border-orange-400 text-gray-400 hover:text-orange-500'
                      }`}
                    >
                      ½
                    </button>

                    {/* Entschuldigt */}
                    <button
                      data-testid={`status-entschuldigt-${t.id}`}
                      title="Abwesend entschuldigt"
                      onClick={() => setStatus(t.id, 'entschuldigt')}
                      className={`w-10 h-9 rounded border-2 flex items-center justify-center transition-all ${
                        anwesenheit[t.id] === 'entschuldigt' 
                          ? 'bg-yellow-500 border-yellow-600 text-white' 
                          : 'border-gray-300 hover:border-yellow-400 text-gray-400 hover:text-yellow-500'
                      }`}
                    >
                      <Clock className="h-4 w-4" />
                    </button>
                    
                    {/* Fehlend */}
                    <button
                      data-testid={`status-fehlend-${t.id}`}
                      title="Abwesend unentschuldigt"
                      onClick={() => setStatus(t.id, 'fehlend')}
                      className={`w-10 h-9 rounded border-2 flex items-center justify-center transition-all ${
                        anwesenheit[t.id] === 'fehlend' 
                          ? 'bg-red-500 border-red-600 text-white' 
                          : 'border-gray-300 hover:border-red-400 text-gray-400 hover:text-red-500'
                      }`}
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                  <div className="p-2 flex justify-center gap-1">
                    {/* Auto-Fill Anwesenheit Button (pro TN) — generiert AZAV-konforme Anwesenheits-Daten */}
                    <button
                      onClick={async () => {
                        if (!window.confirm(`Realistische Anwesenheits-Daten für ${t.vorname} ${t.nachname} generieren?\n\n• 95-97% anwesend\n• 2-3 entschuldigte Tage\n• AZAV-konform\n\nBestehende Daten werden überschrieben.`)) return;
                        const tid = toast.loading(`Generiere Anwesenheit für ${t.vorname} ${t.nachname}...`);
                        try {
                          const res = await axios.post(
                            `${API}/klassenbuch-neu/teilnehmer/${t.id}/auto-fill-anwesenheit`,
                            {}
                          );
                          toast.success(
                            `${res.data.gesamt_tage} Tage gefüllt: ${res.data.anwesend} anwesend, ${res.data.entschuldigt} entschuldigt (${res.data.anwesenheit_prozent}%)`,
                            { id: tid }
                          );
                          loadKursData(selectedKurs.id, datum);
                        } catch (error) {
                          let detail = 'Fehler beim Auto-Füllen';
                          if (error.response?.data?.detail) detail = error.response.data.detail;
                          toast.error(detail, { id: tid });
                        }
                      }}
                      className="px-2 py-1 rounded bg-amber-100 hover:bg-amber-200 text-amber-800 text-xs font-medium flex items-center gap-1 border border-amber-300"
                      data-testid={`btn-auto-fill-anwesenheit-${t.id}`}
                      title={`Auto-Füllen: Realistische AZAV-Anwesenheit für ${t.vorname} ${t.nachname} (95-97% anwesend, 2-3 entschuldigt)`}
                    >
                      <Clock className="h-3 w-3" /> Auto
                    </button>
                    {/* Anwesenheitsliste PDF Download Button (pro TN) */}
                    <button
                      onClick={async () => {
                        toast.info(`Anwesenheitsliste-PDF für ${t.vorname} ${t.nachname} wird erstellt...`);
                        try {
                          const response = await axios.get(
                            `${API}/klassenbuch-neu/teilnehmer/${t.id}/stundenplan-pdf`,
                            { responseType: 'blob' }
                          );
                          const url = window.URL.createObjectURL(new Blob([response.data]));
                          const link = document.createElement('a');
                          link.href = url;
                          link.setAttribute('download', `Anwesenheitsliste_${t.nachname}_${t.vorname}.pdf`);
                          document.body.appendChild(link);
                          link.click();
                          link.remove();
                          window.URL.revokeObjectURL(url);
                          toast.success(`Anwesenheitsliste-PDF heruntergeladen!`);
                        } catch (error) {
                          let detail = 'Fehler beim Erstellen des PDFs';
                          if (error.response?.data instanceof Blob) {
                            try { const t = await error.response.data.text(); const j = JSON.parse(t); detail = j.detail || detail; } catch (e) {}
                          } else if (error.response?.data?.detail) detail = error.response.data.detail;
                          toast.error(detail);
                        }
                      }}
                      className="w-10 h-9 rounded border-2 border-gray-300 hover:border-emerald-400 text-gray-400 hover:text-emerald-500 flex items-center justify-center transition-all"
                      title={`Anwesenheitsliste-PDF (FB_TN_05) für ${t.vorname} ${t.nachname}`}
                    >
                      <FileText className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => downloadPdf(`/anwesenheit-tag/fehlzeiten/${selectedKurs.id}/teilnehmer/${t.id}/pdf`, `Fehlzeiten_${t.nachname}_${t.vorname}.pdf`)}
                      className="px-2 h-9 rounded border-2 border-gray-300 hover:border-orange-400 text-gray-500 hover:text-orange-600 text-xs font-medium"
                      title={`Fehlzeiten-Übersicht für ${t.vorname} ${t.nachname}`}
                      data-testid={`btn-fehlzeiten-tn-${t.id}`}
                    >
                      Fehlz.
                    </button>
                  </div>
                  {anwesenheit[t.id] === 'teilweise' && (
                    <div className="col-span-3">
                      <TeilweiseFelder wert={anwDetails[t.id]} onChange={(v) => setAnwDetails(prev => ({ ...prev, [t.id]: v }))} />
                    </div>
                  )}
                </div>
              );
              })}
            </div>
          )}
        </div>

        {/* Zusammenfassung & Speichern */}
        <div className="p-4 bg-gray-100 border-t-2 border-gray-300">
          <div className="flex justify-between items-center">
            <div className="flex gap-6 text-sm">
              <span className="flex items-center gap-1">
                <span className="w-3 h-3 rounded-full bg-green-500"></span>
                Anwesend: <strong>{stats.anwesend}</strong>
              </span>
              <span className="flex items-center gap-1">
                <span className="w-3 h-3 rounded-full bg-orange-500"></span>
                Teilweise: <strong>{stats.teilweise}</strong>
              </span>
              <span className="flex items-center gap-1">
                <span className="w-3 h-3 rounded-full bg-yellow-500"></span>
                Entschuldigt: <strong>{stats.entschuldigt}</strong>
              </span>
              <span className="flex items-center gap-1">
                <span className="w-3 h-3 rounded-full bg-red-500"></span>
                Fehlend: <strong>{stats.fehlend}</strong>
              </span>
              <span className="text-gray-600">
                | <strong>{stats.gesamt_ue} UE</strong> heute
              </span>
            </div>
            <TagAktionen kursId={selectedKurs.id} datum={datum} tagDoc={tagDoc} onSave={(abschl) => speichern(abschl)} />
          </div>
        </div>
      </div>

      {/* Modal für einzelnen Teilnehmer Klassenbuch bearbeiten - auch in ANSICHT 2 */}
      {showTeilnehmerDialog && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center">
          {/* Overlay */}
          <div 
            className="fixed inset-0 bg-black bg-opacity-50" 
            onClick={() => setShowTeilnehmerDialog(false)}
          />
          {/* Modal Content */}
          <div className="relative bg-white rounded-lg shadow-xl max-w-5xl w-full mx-4 max-h-[90vh] overflow-y-auto z-[10000]">
            {/* Header */}
            <div className="sticky top-0 bg-white border-b px-6 py-4 flex justify-between items-center">
              <h2 className="text-lg font-bold flex items-center gap-2">
                <User className="h-5 w-5 text-blue-600" />
                <span>Klassenbuch: {selectedTeilnehmerForEdit?.vorname || ''} {selectedTeilnehmerForEdit?.nachname || ''}</span>
                {selectedTeilnehmerForEdit && istBildungsgutschein(selectedTeilnehmerForEdit) && (
                  <span className="px-2 py-0.5 text-xs font-bold bg-blue-600 text-white rounded">BG</span>
                )}
              </h2>
              <button 
                onClick={() => setShowTeilnehmerDialog(false)}
                className="text-gray-500 hover:text-gray-700 text-2xl"
              >
                ×
              </button>
            </div>
            
            {/* Body */}
            <div className="p-6 min-h-[200px]">
              {loadingTeilnehmerKlassenbuch ? (
                <div className="text-center py-8 text-gray-500">
                  <div className="animate-spin h-8 w-8 border-4 border-blue-500 border-t-transparent rounded-full mx-auto mb-4"></div>
                  Lade Klassenbuch-Daten...
                </div>
              ) : teilnehmerAnwesenheitEdit.length === 0 ? (
                <div className="text-center py-8">
                  <p className="text-gray-500 mb-4">Noch keine Klassenbuch-Einträge für diesen Teilnehmer.</p>
                  <p className="text-sm text-gray-400 mb-6">
                    Klicken Sie auf "Tag hinzufügen" um Einträge anzulegen.
                  </p>
                  <div className="flex justify-center gap-2">
                    <Button variant="outline" onClick={() => setShowTeilnehmerDialog(false)}>
                      Schließen
                    </Button>
                    <Button 
                      onClick={handleAddTeilnehmerTag}
                      className="bg-blue-600 hover:bg-blue-700 text-white"
                    >
                      <Plus className="h-4 w-4 mr-1" />
                      Tag hinzufügen
                    </Button>
                  </div>
                </div>
              ) : (
              <div className="space-y-4">
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm flex justify-between items-center">
                  <p className="text-blue-700">
                    <strong>{teilnehmerAnwesenheitEdit.length} Unterrichtstage</strong> | 
                    Anwesend: {teilnehmerAnwesenheitEdit.filter(t => t.status === 'anwesend').length} |
                    Entschuldigt: {teilnehmerAnwesenheitEdit.filter(t => t.status === 'entschuldigt').length} |
                    Fehlend: {teilnehmerAnwesenheitEdit.filter(t => t.status === 'fehlend').length}
                  </p>
                  <Button 
                    size="sm" 
                    variant="outline"
                    onClick={handleAddTeilnehmerTag}
                    className="text-blue-600 border-blue-300"
                  >
                    <Plus className="h-3 w-3 mr-1" />
                    Tag hinzufügen
                  </Button>
                </div>
                
                <div className="border rounded-lg overflow-hidden max-h-[50vh] overflow-y-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-100 sticky top-0">
                      <tr>
                        <th className="px-2 py-2 text-left w-10">Tag</th>
                        <th className="px-2 py-2 text-left w-32">Datum</th>
                        <th className="px-2 py-2 text-left">Thema</th>
                        <th className="px-2 py-2 text-center w-20">UE</th>
                        <th className="px-2 py-2 text-center w-28">Status</th>
                        <th className="px-2 py-2 text-center w-10"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {teilnehmerAnwesenheitEdit.map((tag, idx) => (
                        <tr 
                          key={idx} 
                          className={`border-b ${
                            tag.status === 'entschuldigt' ? 'bg-yellow-50' : 
                            tag.status === 'fehlend' ? 'bg-red-50' : ''
                          }`}
                        >
                          <td className="px-2 py-1 text-gray-500 text-center">{tag.tag_im_kurs || idx + 1}</td>
                          <td className="px-2 py-1">
                            <input
                              type="date"
                              value={tag.datum || ''}
                              onChange={(e) => handleChangeTeilnehmerEintrag(idx, 'datum', e.target.value)}
                              className="w-full px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:border-blue-500"
                            />
                          </td>
                          <td className="px-2 py-1">
                            <input
                              type="text"
                              value={tag.thema || ''}
                              onChange={(e) => handleChangeTeilnehmerEintrag(idx, 'thema', e.target.value)}
                              placeholder="Thema eingeben..."
                              className="w-full px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:border-blue-500"
                            />
                          </td>
                          <td className="px-2 py-1">
                            <input
                              type="number"
                              value={tag.ue || 8}
                              onChange={(e) => handleChangeTeilnehmerEintrag(idx, 'ue', parseInt(e.target.value) || 8)}
                              min="1"
                              max="12"
                              className="w-full px-2 py-1 border border-gray-300 rounded text-sm text-center focus:outline-none focus:border-blue-500"
                            />
                          </td>
                          <td className="px-2 py-1">
                            <div className="flex justify-center gap-1">
                              <button
                                onClick={() => handleChangeTeilnehmerAnwesenheit(idx, 'anwesend')}
                                className={`px-2 py-1 rounded text-xs font-medium ${
                                  tag.status === 'anwesend' 
                                    ? 'bg-green-500 text-white' 
                                    : 'bg-gray-200 hover:bg-green-100'
                                }`}
                                title="Anwesend"
                              >
                                ✓
                              </button>
                              <button
                                onClick={() => handleChangeTeilnehmerAnwesenheit(idx, 'entschuldigt')}
                                className={`px-2 py-1 rounded text-xs font-medium ${
                                  tag.status === 'entschuldigt' 
                                    ? 'bg-yellow-500 text-white' 
                                    : 'bg-gray-200 hover:bg-yellow-100'
                                }`}
                                title="Entschuldigt"
                              >
                                E
                              </button>
                              <button
                                onClick={() => handleChangeTeilnehmerAnwesenheit(idx, 'fehlend')}
                                className={`px-2 py-1 rounded text-xs font-medium ${
                                  tag.status === 'fehlend' 
                                    ? 'bg-red-500 text-white' 
                                    : 'bg-gray-200 hover:bg-red-100'
                                }`}
                                title="Fehlend"
                              >
                                ✗
                              </button>
                            </div>
                          </td>
                          <td className="px-2 py-1">
                            <button
                              onClick={() => handleRemoveTeilnehmerTag(idx)}
                              className="text-red-400 hover:text-red-600 p-1"
                              title="Eintrag entfernen"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                
                {/* Footer Buttons */}
                <div className="flex justify-between items-center pt-4 border-t">
                  <Button variant="outline" onClick={() => setShowTeilnehmerDialog(false)}>
                    Schließen
                  </Button>
                  <div className="flex gap-2">
                    <Button 
                      variant="outline"
                      onClick={handleSaveTeilnehmerAnwesenheit}
                    >
                      Änderungen speichern
                    </Button>
                    <Button 
                      onClick={handleDownloadTeilnehmerPdf}
                      className="bg-blue-600 hover:bg-blue-700 text-white"
                    >
                      <FileText className="h-4 w-4 mr-2" />
                      PDF herunterladen
                    </Button>
                  </div>
                </div>
              </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Dozenten-Zuweisungs-Modal (nach Vorlagen-Import) */}
      {showDozentenDialog && dozentenZuKurs && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="dozenten-dialog">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] flex flex-col">
            <div className="p-5 border-b">
              <h2 className="text-lg font-bold text-stone-900">Dozenten zuweisen</h2>
              <p className="text-sm text-stone-500 mt-1">
                <b>{dozentenZuKurs.tage_total} Tage</b> wurden importiert für <b>{dozentenZuKurs.kurs_name}</b>. 
                Weisen Sie pro Tag-Bereich einen Dozenten zu — das spart dem späteren Audit Compliance-Lücken.
              </p>
            </div>
            <div className="p-5 flex-1 overflow-y-auto space-y-3">
              {bulkZuweisungen.map((z, idx) => (
                <div key={idx} className="grid grid-cols-12 gap-2 items-center" data-testid={`zuweisung-row-${idx}`}>
                  <label className="col-span-2 text-xs text-stone-600">Tag</label>
                  <input
                    type="number" min="1" max={dozentenZuKurs.tage_total}
                    value={z.tag_von}
                    onChange={e => {
                      const v = [...bulkZuweisungen];
                      v[idx].tag_von = parseInt(e.target.value) || 1;
                      setBulkZuweisungen(v);
                    }}
                    className="col-span-2 border border-stone-300 rounded px-2 py-1.5 text-sm"
                    data-testid={`zuweisung-tag-von-${idx}`}
                  />
                  <span className="col-span-1 text-center text-stone-400">bis</span>
                  <input
                    type="number" min="1" max={dozentenZuKurs.tage_total}
                    value={z.tag_bis}
                    onChange={e => {
                      const v = [...bulkZuweisungen];
                      v[idx].tag_bis = parseInt(e.target.value) || 1;
                      setBulkZuweisungen(v);
                    }}
                    className="col-span-2 border border-stone-300 rounded px-2 py-1.5 text-sm"
                    data-testid={`zuweisung-tag-bis-${idx}`}
                  />
                  <select
                    value={z.dozent}
                    onChange={e => {
                      const v = [...bulkZuweisungen];
                      v[idx].dozent = e.target.value;
                      setBulkZuweisungen(v);
                    }}
                    className="col-span-4 border border-stone-300 rounded px-2 py-1.5 text-sm"
                    data-testid={`zuweisung-dozent-${idx}`}
                  >
                    {verfuegbareDozenten.map(d => (
                      <option key={d.kuerzel} value={d.kuerzel}>{d.name} ({d.kuerzel})</option>
                    ))}
                  </select>
                  <button
                    onClick={() => setBulkZuweisungen(bulkZuweisungen.filter((_, i) => i !== idx))}
                    disabled={bulkZuweisungen.length === 1}
                    className="col-span-1 text-red-500 hover:text-red-700 disabled:text-stone-300 disabled:cursor-not-allowed"
                    data-testid={`zuweisung-remove-${idx}`}
                    title="Zeile entfernen"
                  >×</button>
                </div>
              ))}
              <button
                onClick={() => {
                  const last = bulkZuweisungen[bulkZuweisungen.length - 1];
                  setBulkZuweisungen([
                    ...bulkZuweisungen,
                    { tag_von: (last?.tag_bis || 0) + 1, tag_bis: Math.min((last?.tag_bis || 0) + 10, dozentenZuKurs.tage_total), dozent: 'MD' }
                  ]);
                }}
                className="text-sm text-blue-600 hover:text-blue-800 font-medium"
                data-testid="zuweisung-add-row"
              >+ Weitere Tag-Range hinzufügen</button>
              <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded text-xs text-blue-900">
                <b>Beispiel:</b> Tag 1–20 → Julia, Tag 21–40 → Markus, Tag 41–66 → Annika. Die Tag-Ranges können sich auch überlappen — spätere Einträge überschreiben frühere.
              </div>
            </div>
            <div className="p-5 border-t flex items-center justify-between gap-3">
              <Button
                variant="outline"
                onClick={() => { setShowDozentenDialog(false); setDozentenZuKurs(null); }}
                data-testid="zuweisung-skip-btn"
              >Später zuweisen</Button>
              <Button
                onClick={async () => {
                  setSavingDozenten(true);
                  try {
                    const res = await axios.put(
                      `${API}/klassenbuch-neu/kurs/${dozentenZuKurs.kurs_id}/bulk-dozent`,
                      { zuweisungen: bulkZuweisungen }
                    );
                    toast.success(`${res.data.updated} Tage aktualisiert`);
                    setShowDozentenDialog(false);
                    setDozentenZuKurs(null);
                    loadKursData(selectedKurs.id, datum);
                  } catch (err) {
                    toast.error(err.response?.data?.detail || 'Fehler beim Speichern');
                  } finally {
                    setSavingDozenten(false);
                  }
                }}
                disabled={savingDozenten}
                className="bg-emerald-600 hover:bg-emerald-700 text-white"
                data-testid="zuweisung-save-btn"
              >{savingDozenten ? 'Speichere...' : 'Dozenten speichern'}</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
